import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Upload, MessageSquare, CheckCircle, Download, Trash2, Edit, Users, Clock, AlertCircle, ChevronDown, FileText, Paperclip, User, X, RotateCcw, FolderOpen, Layers, Edit2, UserPlus, Share2, Link2, Copy } from 'lucide-react';
import { getFileIcon, getFileExtension } from '../utils/fileIcons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import FileSelector from '../components/FileSelector';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Helper function to format duration as "00g:00s:00d"
const formatDuration = (totalMilliseconds) => {
  const totalSeconds = Math.floor(totalMilliseconds / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  
  // Format as "00g:00s:00d"
  const daysStr = String(days).padStart(2, '0');
  const hoursStr = String(hours).padStart(2, '0');
  const minutesStr = String(minutes).padStart(2, '0');
  
  return `${daysStr}g:${hoursStr}s:${minutesStr}d`;
};

export default function TaskDetail({ taskId: propTaskId, isDrawer = false, onModalOpen, onClose, onDataChanged }) {
  const { taskId: paramTaskId } = useParams();
  const taskId = propTaskId || paramTaskId; // Prop varsa onu kullan, yoksa URL'den al
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  // Refs for scrolling
  const commentsRef = useRef(null);
  const filesRef = useRef(null);
  const statusRef = useRef(null);
  const workflowRef = useRef(null);
  const detailsRef = useRef(null);
  const assignmentRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [files, setFiles] = useState([]);
  const fileSummary = useMemo(() => {
    if (files.length === 0) return '';
    const totalBytes = files.reduce((sum, f) => sum + (f.fileSize || 0), 0);
    let sizeStr = '';
    if (totalBytes > 0) {
      if (totalBytes >= 1024 * 1024 * 1024) sizeStr = (totalBytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
      else if (totalBytes >= 1024 * 1024) sizeStr = (totalBytes / (1024 * 1024)).toFixed(2) + ' MB';
      else if (totalBytes >= 1024) sizeStr = (totalBytes / 1024).toFixed(1) + ' KB';
      else sizeStr = totalBytes + ' B';
    }
    return `${files.length} dosya${sizeStr ? ', ' + sizeStr : ''}`;
  }, [files]);
  const [users, setUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]); // Rol bazlı filtrelenmiş kullanıcı listesi (dropdown'lar için)
  const [teams, setTeams] = useState([]); // Takımlar
  const [tags, setTags] = useState([]); // Kategoriler
  const [workflows, setWorkflows] = useState([]); // İş akışları
  const [workflow, setWorkflow] = useState(null);
  const [project, setProject] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [highlightSection, setHighlightSection] = useState(null);
  const [highlightItemId, setHighlightItemId] = useState(null);
  const [recentChanges, setRecentChanges] = useState([]);
  const [showRecentChanges, setShowRecentChanges] = useState(true); // İlk 5 her zaman açık
  const [showAllChanges, setShowAllChanges] = useState(false); // Tüm değişiklikleri göster
  const [readItems, setReadItems] = useState(new Set()); // Okunmuş item ID'leri
  const [showReviewDialog, setShowReviewDialog] = useState(false); // İnceleme dialog'u
  const [reviewOptions, setReviewOptions] = useState([]); // Mevcut inceleme seçenekleri
  const [selectedReviewer, setSelectedReviewer] = useState(''); // Seçilen inceleyici
  const [selectedReviewTagId, setSelectedReviewTagId] = useState(''); // Kontrol için kategori seçimi
  const [reviewNote, setReviewNote] = useState(''); // Kontrol için açıklama/not
  const [showCompleteDialog, setShowCompleteDialog] = useState(false); // Görev tamamlama dialog'u (direkt atama için)
  const [showRevisionDialog, setShowRevisionDialog] = useState(false); // Revizyon dialog'u
  const [revisionReason, setRevisionReason] = useState(''); // Revizyon nedeni
  const [selectedRevisionUsers, setSelectedRevisionUsers] = useState([]); // Seçilen revizyon kullanıcıları (array)
  const [selectedRevisionSteps, setSelectedRevisionSteps] = useState([]); // Seçilen revizyon adımları [{userId, stepIndex}] - çoklu-adım hatasını önler
  const [revisionFiles, setRevisionFiles] = useState([]); // Revizyon dosyaları
  const [sendingRevision, setSendingRevision] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showFileSelector, setShowFileSelector] = useState(false);
  const [showRevisionFileSelector, setShowRevisionFileSelector] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showEditQuantityDialog, setShowEditQuantityDialog] = useState(false); // Miktar düzenleme dialogu
  const [editQuantityValue, setEditQuantityValue] = useState(''); // Sadece miktar değeri
  // Modal kaldırıldı - Kullanıcı zaten iş akışı listesinde bilgileri güncelleyebiliyor
  const [showEditStepQuantityDialog, setShowEditStepQuantityDialog] = useState(false); // Adım miktar düzenleme modal'ı
  const [editStepQuantityData, setEditStepQuantityData] = useState({
    stepIndex: null,
    assignmentIndex: null,
    tagId: '',
    quantityType: 'adet',
    quantityValue: ''
  });
  
  // Direkt atama görevleri için adım listesi
  const [stepQuantities, setStepQuantities] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUsers, setShareUsers] = useState([]);
  const [shareSearchQuery, setShareSearchQuery] = useState('');
  const [sharingUserId, setSharingUserId] = useState(null);
  const [shareNote, setShareNote] = useState('');
  const [editData, setEditData] = useState({ 
    title: '', 
    description: '', 
    priority: 'medium',
    quantityType: 'adet',
    quantityValue: '0',
    tagId: '', // Kategori ID
    workflowId: '' // İş akışı ID
  });
  const [currentTime, setCurrentTime] = useState(new Date()); // Canlı süre güncellemesi için
  
  // Atama değişikliği state'leri
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignData, setReassignData] = useState({
    newUserId: '',
    reason: '',
    assignmentIndex: null, // Hangi assignment değiştiriliyor
    oldUserId: null, // Eski kullanıcı ID
    oldUserName: null, // Eski kullanıcı adı
    stepIndex: null // Hangi adımın değiştirildiği (workflow için kritik!)
  });
  const [reassigning, setReassigning] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);

  // Onay modalları
  const [confirmAction, setConfirmAction] = useState(null); // { type, title, message, onConfirm }

  // Görüntülenme takibi (view tracking)
  const [viewCount, setViewCount] = useState(0); // Toplam (global) görüntülenme
  const [userViewCount, setUserViewCount] = useState(0); // Bu kullanıcının görüntülemesi
  const [showStartTaskModal, setShowStartTaskModal] = useState(false); // "Beklemede" -> "İşlemde" prompt modal
  const viewTrackedRef = useRef(false); // Bu mount'ta görüntülenme tetiklendi mi
  const autoStartTriggeredRef = useRef(false); // Aksiyon ile otomatik başlatma tetiklendi mi

  // Admin/Proje yöneticisi durum ve öncelik değiştirme
  const isAdmin = user?.role === 'admin';
  const canChangePriority = user?.role === 'admin' || user?.role === 'proje_yoneticisi';

  const handleStatusChange = async (newStatus) => {
    setShowStatusDropdown(false);
    if (newStatus === task.status) return;
    try {
      await axios.patch(`${API}/tasks/${task.id}`, { status: newStatus });
      toast.success(`Durum güncellendi: ${statusLabels[newStatus]}`);
      fetchTask();
      if (onDataChanged) onDataChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Durum güncellenemedi');
    }
  };

  const handlePriorityChange = async (newPriority) => {
    setShowPriorityDropdown(false);
    if (newPriority === task.priority) return;
    try {
      await axios.patch(`${API}/tasks/${task.id}`, { priority: newPriority });
      toast.success(`Öncelik güncellendi: ${priorityLabels[newPriority]}`);
      fetchTask();
      if (onDataChanged) onDataChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Öncelik güncellenemedi');
    }
  };

  // Dropdown dışına tıklanınca kapat
  useEffect(() => {
    if (!showStatusDropdown && !showPriorityDropdown) return;
    const handleClickOutside = (e) => {
      if (showStatusDropdown && !e.target.closest('[data-status-dropdown]')) {
        setShowStatusDropdown(false);
      }
      if (showPriorityDropdown && !e.target.closest('[data-priority-dropdown]')) {
        setShowPriorityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStatusDropdown, showPriorityDropdown]);

  // Kullanıcı görev üzerinde atanmış mı (direct atama veya aktif adım) kontrolü.
  const isUserAssignedActive = () => {
    if (!user || !task) return false;
    if (task.assignedTo && task.assignedTo === user.id) return true;
    return (task.assignmentHistory || []).some(
      (a) => a && a.userId === user.id && !a.completedAt
    );
  };

  // Görev "Beklemede" iken, atanmış/aktif kullanıcı bir aksiyon yaparsa
  // (yorum, dosya ekleme, miktar düzenleme vb.) görev sessizce "İşlemde"ye taşınır.
  const autoStartIfPending = async () => {
    if (!task || !user) return;
    if (task.status !== 'beklemede') return;
    if (!isUserAssignedActive()) return;
    if (autoStartTriggeredRef.current) return;
    autoStartTriggeredRef.current = true;
    try {
      await axios.patch(`${API}/tasks/${task.id}`, { status: 'işlemde' });
      setShowStartTaskModal(false);
      // Sessizce başarı bildirimi (kullanıcı aksiyon yaptığı için bağlam yeterli)
      toast.success('Görev otomatik olarak İşlemde\'ye taşındı');
    } catch (error) {
      // Sessiz hata: kullanıcının aksiyonunu engellememeli
      console.warn('Otomatik İşlemde\'ye taşıma başarısız:', error);
      autoStartTriggeredRef.current = false;
    }
  };

  // Modal'da "Evet, başlat" butonu
  const handleConfirmStartTask = async () => {
    if (!task) return;
    try {
      await axios.patch(`${API}/tasks/${task.id}`, { status: 'işlemde' });
      setShowStartTaskModal(false);
      toast.success('Görev İşlemde\'ye taşındı');
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Görev başlatılamadı');
    }
  };

  // Paylaşım fonksiyonları
  const handleOpenShareModal = async () => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/share-users`);
      setShareUsers(response.data);
      setShareSearchQuery('');
      setSharingUserId(null);
      setShareNote('');
      setShowShareModal(true);
    } catch (error) {
      toast.error('Kullanıcı listesi yüklenemedi');
    }
  };

  const handleShareTask = async (targetUserId) => {
    if (!shareNote.trim()) {
      toast.error('Paylaşım notu zorunludur');
      return;
    }
    setSharingUserId(targetUserId);
    try {
      await axios.post(`${API}/tasks/${taskId}/share`, { userId: targetUserId, note: shareNote.trim() });
      const targetUser = shareUsers.find(u => u.id === targetUserId);
      toast.success(`Görev ${targetUser?.name || ''} ile paylaşıldı`);
      setShowShareModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Paylaşım başarısız');
    } finally {
      setSharingUserId(null);
    }
  };

  useEffect(() => {
    // Önceki istekleri iptal et
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Her görev açılışında view tracking ref'lerini sıfırla
    viewTrackedRef.current = false;
    autoStartTriggeredRef.current = false;

    const loadData = async () => {
      try {
        await fetchTask(controller.signal);
        if (controller.signal.aborted) return;
        await Promise.all([
          fetchComments(controller.signal),
          fetchFiles(controller.signal),
          fetchUsers(controller.signal),
          fetchTeams(controller.signal),
          fetchTags(controller.signal),
          fetchWorkflows(controller.signal)
        ]);

        // Görüntülenme takibini gönder (idempotent guard ile)
        if (!viewTrackedRef.current && !controller.signal.aborted) {
          viewTrackedRef.current = true;
          try {
            const viewRes = await axios.post(`${API}/tasks/${taskId}/view`);
            setViewCount(viewRes.data?.viewCount || 0);
            setUserViewCount(viewRes.data?.userViewCount || 0);
          } catch (err) {
            // View tracking hatası kritik değil, sessizce yut
            console.warn('View tracking başarısız:', err);
          }
        }
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
        console.error('Görev verileri yüklenirken hata:', error);
      }
    };
    
    loadData();
    
    // Görev açıldığında bildirimleri okundu işaretle (3 saniye sonra)
    const timer = setTimeout(() => {
      markTaskNotificationsRead();
    }, 3000);
    
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [taskId]);

  // Canlı süre güncellemesi için her dakika state'i güncelle
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Her 60 saniyede bir güncelle
    
    return () => clearInterval(interval);
  }, []);


  // Fetch recent changes after task is loaded
  useEffect(() => {
    if (task) {
      fetchRecentChanges();
    }
  }, [task, comments, files]);

  // "Beklemede" durumundaki görev için "Başlat?" modalını göster.
  // Koşullar:
  //   - Görev status === "beklemede"
  //   - Kullanıcı atanmış kullanıcı (direct assignee) VEYA aktif adımdaki kullanıcı
  //   - userViewCount > 0 (yani view endpoint çağrıldıktan sonra)
  //   - Modal halen kapalı
  // Her görev açılışında (mount), beklemede iken modal yeniden gösterilir.
  useEffect(() => {
    if (!task || !user) return;
    if (task.status !== 'beklemede') {
      setShowStartTaskModal(false);
      return;
    }
    // Eğer kullanıcı otomatik aksiyon ile başlatma sürecindeyse modalı açma
    if (autoStartTriggeredRef.current) return;

    // Direct assignment kontrolü
    const isDirectAssignee = task.assignedTo && task.assignedTo === user.id;
    // Workflow aktif adım kontrolü (completedAt === null olan)
    const hasActiveAssignment = (task.assignmentHistory || []).some(
      (a) => a && a.userId === user.id && !a.completedAt
    );

    if ((isDirectAssignee || hasActiveAssignment) && userViewCount > 0) {
      setShowStartTaskModal(true);
    }
  }, [task, user, userViewCount]);

  // Handle URL query parameters for highlighting and scrolling
  useEffect(() => {
    if (!task) return;
    
    const highlight = searchParams.get('highlight');
    const itemId = searchParams.get('id');
    const step = searchParams.get('step');
    
    if (highlight) {
      setHighlightSection(highlight);
      setHighlightItemId(itemId);
      
      // Wait for DOM to be ready, then scroll
      setTimeout(() => {
        let targetRef = null;
        
        switch (highlight) {
          case 'comments':
            targetRef = commentsRef;
            break;
          case 'files':
            targetRef = filesRef;
            break;
          case 'status':
            targetRef = statusRef;
            break;
          case 'workflow':
            targetRef = workflowRef;
            break;
          case 'details':
            targetRef = detailsRef;
            break;
          case 'assignment':
            targetRef = assignmentRef;
            break;
        }
        
        if (targetRef?.current) {
          targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        // Clear highlight after 10 seconds
        setTimeout(() => {
          setHighlightSection(null);
          setHighlightItemId(null);
        }, 10000);
      }, 500);
    }
  }, [task, searchParams]);

  // IntersectionObserver for marking items as read when user scrolls to them
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5 // Item'in %50'si görünür olduğunda
    };

    const callback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const itemId = entry.target.getAttribute('data-item-id');
          const itemType = entry.target.getAttribute('data-item-type');
          
          if (itemId && !readItems.has(itemId)) {
            // Mark as read
            setReadItems(prev => new Set([...prev, itemId]));
            
            // Send to backend to mark notification as read
            markItemAsRead(itemId, itemType);
          }
        }
      });
    };

    const observer = new IntersectionObserver(callback, options);

    // Observe all comment and file elements
    const commentElements = document.querySelectorAll('[data-item-type="comment"]');
    const fileElements = document.querySelectorAll('[data-item-type="file"]');
    
    commentElements.forEach(el => observer.observe(el));
    fileElements.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, [comments, files, readItems]);

  const markItemAsRead = async (itemId, itemType) => {
    try {
      // Mark the notification for this specific item as read
      await axios.post(`${API}/notifications/mark-item-read`, {
        taskId: taskId,
        changeId: itemId,
        changeType: itemType
      });
      console.log(`Marked ${itemType} ${itemId} as read`);
    } catch (error) {
      console.error('Failed to mark item as read:', error);
    }
  };

  // İnceleme seçeneklerini hazırla
  const prepareReviewOptions = async () => {
    try {
      // Yeni endpoint'ten hiyerarşik üst yetkilileri getir
      await fetchAvailableReviewers();
    } catch (error) {
      console.error('Failed to prepare review options:', error);
      toast.error('İnceleme seçenekleri hazırlanamadı');
    }
  };

  // İnceleme için gönder - yeni endpoint'ten üst yetkilileri getir
  const fetchAvailableReviewers = async () => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/available-reviewers`);
      const sorted = [...response.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setReviewOptions(sorted);
    } catch (error) {
      console.error('Failed to fetch reviewers:', error);
      toast.error('İnceleyiciler yüklenemedi');
    }
  };

  const handleSendForReview = async () => {
    if (!selectedReviewer) {
      toast.error('Lütfen bir inceleyici seçin');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/tasks/${taskId}/send-for-review`, {
        reviewerId: selectedReviewer,
        reviewNote: reviewNote || null  // İsteğe bağlı açıklama
        // reviewTagId gönderilmiyor - backend otomatik "KONTROL" kategorisini bulacak
      });
      
      toast.success(response.data.message);
      setShowReviewDialog(false);
      setShowCompleteDialog(false);  // Yeni dialog'u da kapat
      setSelectedReviewer('');
      setReviewNote('');  // Açıklama sıfırla
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Görev gönderilemedi');
    }
  };

  const markTaskNotificationsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-task-read/${taskId}`);
    } catch (error) {
      console.error('Mark notifications read failed:', error);
    }
  };

  const fetchRecentChanges = async () => {
    if (!task) return;
    
    console.log('🔍 Fetching recent changes for task:', taskId);
    
    try {
      // Get recent comments
      const commentsRes = await axios.get(`${API}/comments/${taskId}`);
      const allComments = commentsRes.data || [];
      console.log('📝 Comments fetched:', allComments.length);
      
      // Get recent files
      const filesRes = await axios.get(`${API}/tasks/${taskId}/files`);
      const allFiles = filesRes.data || [];
      console.log('📎 Files fetched:', allFiles.length);
      
      // Get task history
      let taskHistory = [];
      try {
        const historyRes = await axios.get(`${API}/tasks/${taskId}/history`);
        taskHistory = historyRes.data || [];
        console.log('📜 History fetched:', taskHistory.length);
      } catch (err) {
        console.log('⚠️  History not available:', err.message);
      }
      
      // Combine all changes
      const allChanges = [];
      const seenChangeIds = new Set(); // Duplicate kontrolü için
      
      // Önce history'yi ekle (en önemli ve detaylı veri)
      taskHistory.slice(0, 5).forEach(h => {
        console.log('🔍 History item changeType:', h.changeType, 'changeId:', h.changeId, 'action:', h.action);
        const uniqueKey = `${h.changeType}_${h.changeId}`;
        if (!seenChangeIds.has(uniqueKey)) {
          seenChangeIds.add(uniqueKey);
          allChanges.push({ 
            ...h, 
            type: 'history',
            changeType: h.changeType,
            displayText: h.action,
            createdAt: h.changedAt 
          });
        }
      });
      
      // Sonra comments'leri ekle (history'de olmayanlar)
      allComments.slice(0, 3).forEach(c => {
        const uniqueKey = `comment_${c.id}`;
        if (!seenChangeIds.has(uniqueKey)) {
          seenChangeIds.add(uniqueKey);
          allChanges.push({ 
            ...c, 
            type: 'comment', 
            displayText: 'Yeni yorum',
            createdAt: c.createdAt 
          });
        }
      });
      
      // Son olarak files'ları ekle (history'de olmayanlar)
      allFiles.slice(0, 3).forEach(f => {
        const uniqueKey = `file_${f.id}`;
        if (!seenChangeIds.has(uniqueKey)) {
          seenChangeIds.add(uniqueKey);
          allChanges.push({ 
            ...f, 
            type: 'file', 
            displayText: `Dosya eklendi: ${f.filename}`,
            createdAt: f.createdAt || f.uploadedAt 
          });
        }
      });
      
      // Sort by date (newest first) and take last 5
      allChanges.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      console.log('✅ Recent changes combined:', allChanges.slice(0, 5));
      console.log('📊 Setting recentChanges state with', allChanges.slice(0, 5).length, 'items');
      setRecentChanges(allChanges.slice(0, 5));
    } catch (error) {
      console.error('❌ Failed to fetch recent changes:', error);
    }
  };

  const fetchTask = async (signal) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}`, { signal });
      setTask(response.data);
      // viewCount/viewLog mevcut değerleri (view tracking endpoint çağrılana kadar)
      setViewCount(response.data.viewCount || 0);
      
      // Fetch project to get file size limit
      if (response.data.projectId) {
        const projectRes = await axios.get(`${API}/projects/${response.data.projectId}`, { signal });
        setProject(projectRes.data);
      }
      
      // Fetch workflow if exists (check for valid workflowId)
      if (response.data.workflowId && response.data.workflowId !== 'none' && response.data.workflowId !== '') {
        try {
          const workflowRes = await axios.get(`${API}/workflows`, { signal });
          const wf = workflowRes.data.find(w => w.id === response.data.workflowId);
          if (wf) {
            setWorkflow(wf);
            console.log('Workflow loaded:', wf);
          } else {
            console.log('Workflow not found for ID:', response.data.workflowId);
          }
        } catch (error) {
          console.error('Failed to fetch workflow:', error);
        }
      } else {
        console.log('No valid workflowId in task:', response.data.workflowId);
        
        // Direkt atama göreviyse stepQuantities'i yükle veya başlat
        if (!response.data.workflowId) {
          const quantities = response.data.stepQuantities || [];
          
          // Eğer boşsa, görev geçmişinden otomatik oluştur
          if (quantities.length === 0 && response.data.assignmentHistory && response.data.assignmentHistory.length > 0) {
            // Benzersiz KULLANICILAR (userId bazında, stepName'e bakmadan)
            const uniqueUsers = {};
            
            response.data.assignmentHistory.forEach((assignment) => {
              // Key sadece userId olsun - stepName farklı bile olsa aynı kullanıcı tek kayıt
              const key = assignment.userId;
              
              // Eğer bu kullanıcı daha önce eklenmemişse ekle
              if (!uniqueUsers[key] && assignment.userId) {
                uniqueUsers[key] = {
                  stepIndex: Object.keys(uniqueUsers).length,
                  stepName: assignment.stepName || (assignment.stepType === 'Kontrol' ? 'Kontrol' : 'Üretim'),
                  stepType: assignment.stepType || 'Üretim',
                  userId: assignment.userId,
                  tagId: assignment.completedTagId || '',
                  quantityType: assignment.completedQuantityType || 'adet',
                  quantityValue: assignment.completedQuantityValue || ''
                };
              }
            });
            
            const autoGenerated = Object.values(uniqueUsers);
            setStepQuantities(autoGenerated);
          } else {
            setStepQuantities(quantities);
          }
        }
      }
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      toast.error('Görev yüklenemedi');
    }
  };

  const fetchComments = async (signal) => {
    try {
      const response = await axios.get(`${API}/comments/${taskId}`, { signal });
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const fetchFiles = async (signal) => {
    try {
      // Bu görevin dosyalarını al
      const taskFilesResponse = await axios.get(`${API}/tasks/${taskId}/files`, { signal });
      const taskFiles = taskFilesResponse.data;
      
      // Hafif usage endpoint ile kullanım bilgisini al
      const usageResponse = await axios.get(`${API}/tasks/${taskId}/files/usage`, { signal });
      const usageData = usageResponse.data || {};
      
      // Her dosya için kullanım bilgisini ekle
      const enrichedFiles = taskFiles.map(taskFile => {
        const usage = taskFile.s3Key ? usageData[taskFile.s3Key] : null;
        return {
          ...taskFile,
          usedInTasks: usage ? usage.tasks.filter(t => t.taskId !== taskId) : [],
          usageCount: usage ? usage.usageCount : 1
        };
      });
      
      setFiles(enrichedFiles);
    } catch (error) {
      console.error('Failed to fetch files:', error);
      try {
        const taskFilesResponse = await axios.get(`${API}/tasks/${taskId}/files`);
        setFiles(taskFilesResponse.data);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    }
  };

  const handleDeleteFile = async (fileId) => {
    // Yetki kontrolü - admin, can_delete_files veya dosya sahibi (backend'de de kontrol ediliyor)
    const canDelete = user?.role === 'admin' || user?.permissions?.includes('can_delete_files');
    // Dosya sahibi kontrolü backend tarafında yapılacak
    if (!canDelete) {
      // Dosya sahibi olabilir, backend karar versin - direkt gönder
    }

    if (!window.confirm('Bu dosyayı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await axios.delete(`${API}/files/${fileId}`);
      toast.success('Dosya silindi');
      fetchFiles(); // Dosya listesini yenile
      fetchTask(); // Revizyon geçmişini de yenile
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(error.response?.data?.detail || 'Dosya silinirken bir hata oluştu');
    }
  };

  const fetchUsers = async (signal) => {
    try {
      const [allRes, availableRes] = await Promise.all([
        axios.get(`${API}/users`, { signal }),
        axios.get(`${API}/reports/available-users`, { signal })
      ]);
      setUsers(allRes.data);
      const sortedAvailable = [...availableRes.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setAvailableUsers(sortedAvailable);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchTeams = async (signal) => {
    try {
      const response = await axios.get(`${API}/teams`, { signal });
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchTags = async (signal) => {
    try {
      const response = await axios.get(`${API}/tags`, { signal });
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchWorkflows = async (signal) => {
    try {
      const response = await axios.get(`${API}/workflows`, { signal });
      setWorkflows(response.data);
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
    }
  };

  const handleCheckoutClick = async () => {
    // Modal kaldırıldı - Direkt tamamlama yapılıyor
    // Kullanıcı zaten iş akışı listesinde adım bilgilerini güncelleyebiliyor
    try {
      const response = await axios.post(`${API}/tasks/${taskId}/next-step`);
      toast.success(response.data.message === "Task completed" 
        ? 'Görev tamamlandı!' 
        : 'Görev sonraki adıma taşındı');
      fetchTask();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleUpdateStepQuantity = async () => {
    try {
      const response = await axios.put(
        `${API}/tasks/${taskId}/assignments/${editStepQuantityData.assignmentIndex}/quantity`,
        {
          completedTagId: editStepQuantityData.tagId,
          completedQuantityType: editStepQuantityData.quantityType,
          completedQuantityValue: editStepQuantityData.quantityValue
        }
      );
      toast.success('Adım miktarı güncellendi');
      setShowEditStepQuantityDialog(false);
      fetchTask();
      autoStartIfPending();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const handleSendRevision = async (e) => {
    e.preventDefault();
    
    if (!revisionReason.trim()) {
      toast.error('Lütfen revizyon nedeni girin');
      return;
    }
    
    if (selectedRevisionUsers.length === 0) {
      toast.error('Lütfen en az bir kullanıcı seçin');
      return;
    }
    
    setSendingRevision(true);
    
    try {
      const formData = new FormData();
      formData.append('sendToUserIds', JSON.stringify(selectedRevisionUsers));
      // ✅ FIX: Seçilen kullanıcının HANGİ adımına gönderildiğini de ilet (çoklu-adım hatası).
      formData.append('sendToSteps', JSON.stringify(selectedRevisionSteps));
      formData.append('reason', revisionReason);
      
      // Dosyalar varsa ekle - mevcut ve yeni dosyaları ayır
      const existingFileIds = [];
      revisionFiles.forEach(file => {
        if (file.isExisting && file.existingFileId) {
          // Mevcut dosya - ID'sini ayrı gönder
          existingFileIds.push(file.existingFileId);
        } else {
          // Yeni dosya - normal upload
          formData.append('files', file);
        }
      });
      
      if (existingFileIds.length > 0) {
        formData.append('existingFileIds', JSON.stringify(existingFileIds));
      }
      
      await axios.post(`${API}/tasks/${taskId}/send-for-revision`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setShowRevisionDialog(false);
      setRevisionReason('');
      setSelectedRevisionUsers([]);
      setSelectedRevisionSteps([]);
      setRevisionFiles([]);
      toast.success('Görev revizyona gönderildi');
      fetchTask();
      fetchRecentChanges();
    } catch (error) {
      console.error('Revizyon gönderme hatası:', error);
      toast.error(error.response?.data?.detail || 'Revizyon gönderilemedi');
    } finally {
      setSendingRevision(false);
    }
  };

  const handleResolveRevision = async () => {
    if (!task.currentRevision) {
      toast.error('Aktif revizyon bulunamadı');
      return;
    }
    
    try {
      await axios.post(`${API}/tasks/${taskId}/revisions/${task.currentRevision}/resolve`);
      toast.success('Revizyon tamamlandı');
      fetchTask();
      fetchRecentChanges();
    } catch (error) {
      console.error('Revizyon tamamlama hatası:', error);
      toast.error(error.response?.data?.detail || 'Revizyon tamamlanamadı');
    }
  };

  const handleRevisionFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    setRevisionFiles(files);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API}/comments`, {
        taskId,
        content: newComment
      });
      setNewComment('');
      fetchComments();
      toast.success('Yorum eklendi');
      autoStartIfPending();
    } catch (error) {
      toast.error('Yorum eklenemedi');
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editingCommentContent.trim()) return;
    try {
      await axios.patch(`${API}/comments/${commentId}`, { content: editingCommentContent });
      setEditingCommentId(null);
      setEditingCommentContent('');
      fetchComments();
      toast.success('Yorum güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yorum güncellenemedi');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Bu yorumu silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/comments/${commentId}`);
      fetchComments();
      toast.success('Yorum silindi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yorum silinemedi');
    }
  };

  const canEditComment = (comment) => {
    return user?.role === 'admin' || user?.role === 'proje_yoneticisi' || comment.userId === user?.id;
  };


  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Get max file size (default 50MB)
    const maxFileSizeMB = project?.max_file_size_mb || 50;
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;
    const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
    
    // Check file size
    if (file.size > maxFileSizeBytes) {
      toast.error(`Dosya boyutu (${fileSizeMB} MB) proje limitini (${maxFileSizeMB} MB) aşıyor`);
      e.target.value = ''; // Reset input
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/tasks/${taskId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      toast.success('Dosya başarıyla yüklendi');
      fetchFiles();
      setShowFileSelector(false); // Modal'ı kapat
      e.target.value = ''; // Reset input
      autoStartIfPending();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Dosya yüklenemedi';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleAttachExistingFile = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file_id', file.id);
      
      await axios.post(`${API}/tasks/${taskId}/files/attach-existing`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Dosya başarıyla eklendi');
      fetchFiles();
      setShowFileSelector(false); // Modal'ı kapat
      autoStartIfPending();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Dosya eklenemedi';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const getUserName = (userId) => {
    const u = users.find(user => user.id === userId);
    return u ? u.name : 'Bilinmeyen';
  };

  const handleEditTask = () => {
    setEditData({
      title: task.title,
      description: task.description || '',
      priority: task.priority || 'medium',
      quantityType: task.quantityType || 'adet',
      quantityValue: task.quantityValue || '0',
      tagId: task.tagId || '', // Mevcut kategori ID'si
      workflowId: task.workflowId || '' // Mevcut iş akışı ID'si
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/tasks/${taskId}`, {
        title: editData.title,
        description: editData.description
      });
      await fetchTask(); // Veriyi yeniden çek (await ile bekle)
      setShowEditDialog(false);
      toast.success('Görev güncellendi');
    } catch (error) {
      toast.error('Görev güncellenemedi: ' + (error.response?.data?.detail || 'Hata'));
    }
  };

  const canEdit = () => {
    if (!user || !task) return false;
    
    // Sadece Admin ve Proje Yöneticisi düzenleyebilir
    if (user.role === 'admin') return true;
    if (user.role === 'proje_yoneticisi') return true;
    
    return false;
  };

  // Kullanıcı görev geçmişinde varsa veya şu anda atanmışsa veya üst yetkili ise miktar değerini düzenleyebilir
  const canEditQuantity = () => {
    if (!user || !task) return false;
    
    // Admin her zaman düzenleyebilir
    if (user.role === 'admin') return true;
    
    // Proje yöneticisi bu projenin yöneticisiyse düzenleyebilir
    if (user.role === 'proje_yoneticisi' && project && project.managerIds && project.managerIds.includes(user.id)) {
      return true;
    }
    
    // Takım lideri bu görevdeki kullanıcının takım lideriyse düzenleyebilir
    if (user.role === 'takim_lideri' && task.assignedTo) {
      const assignedUser = users.find(u => u.id === task.assignedTo);
      if (assignedUser && assignedUser.teamIds && user.leadTeamIds) {
        const hasCommonTeam = assignedUser.teamIds.some(teamId => user.leadTeamIds.includes(teamId));
        if (hasCommonTeam) return true;
      }
    }
    
    // Şu anda atanmışsa
    if (task.assignedTo === user.id) return true;
    
    // Görev geçmişinde varsa
    if (task.assignmentHistory && task.assignmentHistory.length > 0) {
      const hasWorkedOnTask = task.assignmentHistory.some(h => h.userId === user.id);
      if (hasWorkedOnTask) return true;
    }
    
    return false;
  };

  const canSendRevision = () => {
    if (!user || !task) return false;
    
    // Eğer zaten revizyon modundaysa, HİÇ KİMSE (admin dahil) tekrar revizyon gönderemez
    if (task.currentRevision) {
      return false;
    }
    
    // Workflow'da en az 1 kişi görevi tamamlamış olmalı (completedAt !== null)
    // Yoksa revizyona gönderecek kimse yok
    if (task.assignmentHistory && task.assignmentHistory.length > 0) {
      const hasCompletedAssignment = task.assignmentHistory.some(h => h.completedAt !== null);
      if (!hasCompletedAssignment) {
        return false; // Henüz kimse tamamlamamış
      }
    } else {
      return false; // Hiç atama geçmişi yok
    }
    
    // can_send_revision yetkisi olanlar gönderebilir
    if (user.permissions && user.permissions.includes('can_send_revision')) {
      return true;
    }
    
    // Atanan kullanıcı gönderebilir
    if (task.assignedTo === user.id) {
      return true;
    }
    
    return false;
  };

  const canResolveRevision = () => {
    if (!user || !task) return false;
    
    // Revizyon modunda değilse gösterme (currentRevision kontrolü)
    if (!task.currentRevision) return false;
    
    // can_resolve_revision yetkisi olanlar veya atanan kullanıcı tamamlayabilir
    if (user.permissions?.includes('can_resolve_revision') || task.assignedTo === user.id) {
      return true;
    }
    
    return false;
  };

  const handleEditQuantity = () => {
    setEditQuantityValue(task.quantityValue || '');
    setShowEditQuantityDialog(true);
  };

  const handleSaveQuantity = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/tasks/${taskId}`, {
        quantityValue: editQuantityValue
      });
      setShowEditQuantityDialog(false);
      toast.success('Miktar güncellendi');
      fetchTask(); // Görevi yeniden yükle
    } catch (error) {
      console.error('Miktar güncelleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Miktar güncellenemedi');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    in_review: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800'
  };

  const statusHexColors = {
    pending: '#6b7280',      // Gri (Kanban'daki beklemede rengi)
    beklemede: '#6b7280',    // Gri
    in_progress: '#3b82f6',  // Mavi (Kanban'daki işlemde rengi)
    işlemde: '#3b82f6',      // Mavi
    in_review: '#eab308',    // Sarı (Kanban'daki incelemede rengi)
    incelemede: '#eab308',   // Sarı
    revizyon_gerekli: '#f59e0b', // Turuncu (Revizyon gerekli)
    completed: '#22c55e',    // Yeşil (Kanban'daki tamamlandı rengi)
    tamamlandı: '#22c55e'    // Yeşil
  };

  const statusLabels = {
    pending: 'Beklemede',
    beklemede: 'Beklemede',
    in_progress: 'İşlemde',
    işlemde: 'İşlemde',
    in_review: 'İncelemede',
    incelemede: 'İncelemede',
    revizyon_gerekli: 'Revizyon Gerekli',
    completed: 'Tamamlandı',
    tamamlandı: 'Tamamlandı'
  };

  const priorityColors = {
    low: 'text-green-600',      // Yeşil - Dashboard ile aynı
    medium: 'text-yellow-600',  // Sarı - Dashboard ile aynı
    high: 'text-orange-600',    // Turuncu - Dashboard ile aynı
    urgent: 'text-red-800'      // Koyu kırmızı - Dashboard ile aynı
  };

  const priorityHexColors = {
    low: '#22c55e',      // Yeşil
    medium: '#eab308',   // Sarı
    high: '#f97316',     // Turuncu
    urgent: '#ef4444'    // Kırmızı
  };

  const priorityLabels = {
    low: 'Düşük',
    medium: 'Orta',
    high: 'Yüksek',
    urgent: 'Acil'
  };

  // Check if user can delete task
  const canDeleteTask = () => {
    if (!user || !task) return false;
    // can_delete_tasks iznine sahip olanlar silebilir
    return user.permissions?.includes('can_delete_tasks');
  };

  const handleDeleteTask = async () => {
    if (!canDeleteTask()) {
      toast.error('Bu görevi silme yetkiniz yok');
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Görev silindi');
      setShowDeleteDialog(false);
      // Arka plan verisini sessizce yenile (loading state tetiklemeden)
      if (onDataChanged) onDataChanged();
      // Drawer'ı kapat
      if (isDrawer && onClose) {
        onClose();
      } else {
        navigate('/tasks');
      }
    } catch (error) {
      console.error('Delete task error:', error);
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    } finally {
      setDeleting(false);
    }
  };

  // Atama değişikliği fonksiyonu
  const handleReassign = async () => {
    if (!reassignData.newUserId) {
      toast.error('Lütfen yeni kullanıcı seçin');
      return;
    }

    setReassigning(true);
    try {
      const endpoint = task.workflowId 
        ? `${API}/tasks/${taskId}/reassign-step`
        : `${API}/tasks/${taskId}/reassign`;
      
      const payload = {
        newUserId: reassignData.newUserId,
        reason: reassignData.reason || null
      };
      
      // Workflow için stepIndex ekle (hangi adımın değiştirildiği bilgisi)
      if (task.workflowId && reassignData.stepIndex !== null) {
        payload.stepIndex = reassignData.stepIndex;
      }
      
      console.log('[REASSIGN] Sending request:', { endpoint, payload });
      
      const response = await axios.patch(endpoint, payload);

      toast.success(response.data.message || 'Atama başarıyla değiştirildi');
      
      // Dialog'u kapat ve form'u temizle
      setShowReassignDialog(false);
      setReassignData({ newUserId: '', reason: '', assignmentIndex: null, oldUserId: null, oldUserName: null, stepIndex: null });
      
      // Görev bilgilerini yeniden yükle
      await fetchTask();
      
    } catch (error) {
      console.error('Reassign error:', error);
      toast.error(error.response?.data?.detail || 'Atama değişikliği başarısız');
    } finally {
      setReassigning(false);
    }
  };

  // Enhanced linkify function to handle line breaks and basic formatting
  const formatComment = (text) => {
    if (!text) return '';
    
    const DOMPurify = require('dompurify');
    
    // Önce basit HTML escape (güvenlik için)
    text = text.replace(/&/g, '&amp;')
               .replace(/</g, '&lt;')
               .replace(/>/g, '&gt;')
               .replace(/\r/g, '');
    
    // 1. Satır bazlı işlemler (başlık ve madde listesi)
    const lines = text.split('\n');
    const processedLines = lines.map(line => {
      // Başlık: ## metin veya ##metin
      if (/^##\s*(.+)$/.test(line)) {
        return line.replace(/^##\s*(.+)$/, '<strong class="text-base font-bold block mt-1">$1</strong>');
      }
      // Madde listesi: - metin, -metin veya • metin
      if (/^[-•]\s*(.+)$/.test(line)) {
        return line.replace(/^[-•]\s*(.+)$/, '<span class="flex items-start gap-1"><span class="text-gray-500 select-none">•</span><span>$1</span></span>');
      }
      return line;
    });
    text = processedLines.join('<br>');
    
    // 2. URL'leri linkleştir
    const urlPattern = /(https?:\/\/[^\s<]+)/g;
    text = text.replace(urlPattern, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">$1</a>');
    
    // 3. Inline formatting
    text = text.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>');
    text = text.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic">$1</em>');
    // DOMPurify ile sanitize et
    const clean = DOMPurify.sanitize(text, {
      ALLOWED_TAGS: ['br', 'a', 'strong', 'em', 'span'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    });
    
    return { __html: clean };
  };

  // Helper function to format time ago
  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Az önce';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} dakika önce`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} saat önce`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} gün önce`;
    
    return date.toLocaleDateString('tr-TR');
  };

  // Calculate time spent on a workflow step
  const calculateStepDuration = (stepIndex) => {
    if (!task.assignmentHistory || task.assignmentHistory.length === 0) {
      return null;
    }

    // Eğer görev henüz "işlemde" durumuna geçmediyse süre gösterme
    if (!task.firstStartedAt) {
      return null;
    }

    // İlk adım için özel mantık
    const isFirstStep = stepIndex === 0 || (stepIndex === -1 && task.assignmentHistory[0]?.stepIndex === -1);
    
    // O adıma ait tüm assignment kayıtlarını bul
    const stepAssignments = task.assignmentHistory.filter(h => h.stepIndex === stepIndex);
    
    if (stepAssignments.length === 0) {
      return null;
    }

    // Toplam süreyi hesapla
    let totalMilliseconds = 0;
    
    for (let i = 0; i < stepAssignments.length; i++) {
      const assignment = stepAssignments[i];
      
      // Başlangıç zamanı: İlk adım için firstStartedAt kullan, diğerleri için assignedAt
      let startTime;
      if (isFirstStep && i === 0) {
        // İlk adımın ilk assignment'ı için firstStartedAt kullan
        startTime = new Date(task.firstStartedAt);
      } else {
        startTime = new Date(assignment.assignedAt);
      }
      
      // Bitiş zamanı: completedAt varsa onu kullan, yoksa şu anki zamanı kullan (devam ediyor)
      let endTime;
      if (assignment.completedAt) {
        endTime = new Date(assignment.completedAt);
      } else {
        // Hala devam ediyorsa şu anki zamanı kullan
        endTime = new Date();
      }
      
      totalMilliseconds += (endTime - startTime);
    }

    // Süreyi okunabilir formata çevir
    return formatDuration(totalMilliseconds);
  };

  // Calculate time spent for a single assignment (manuel atamalar için)
  const calculateAssignmentDuration = (assignment, isFirst = false, assignmentIndex = 0) => {
    // Başlangıç zamanı belirleme
    let startTime;
    let endTime;
    
    // Eğer completedAt varsa → Tamamlanmış atama
    if (assignment.completedAt) {
      // Başlangıç zamanı: İlk atama ve firstStartedAt varsa firstStartedAt, yoksa assignedAt
      if (isFirst && task.firstStartedAt) {
        startTime = new Date(task.firstStartedAt);
      } else {
        startTime = new Date(assignment.assignedAt);
      }
      
      endTime = new Date(assignment.completedAt);
      const totalMilliseconds = endTime - startTime;
      return formatDuration(totalMilliseconds);
    }
    
    // completedAt yoksa → Henüz tamamlanmamış atama
    // Bir sonraki atama var mı kontrol et
    const nextAssignment = task.assignmentHistory && task.assignmentHistory[assignmentIndex + 1];
    
    if (nextAssignment) {
      // Bir sonraki atama varsa, bu atama tamamlanmış demektir
      // Süre: assignment.assignedAt → nextAssignment.assignedAt
      if (isFirst && task.firstStartedAt) {
        startTime = new Date(task.firstStartedAt);
      } else {
        startTime = new Date(assignment.assignedAt);
      }
      
      endTime = new Date(nextAssignment.assignedAt);
      const totalMilliseconds = endTime - startTime;
      return formatDuration(totalMilliseconds);
    }
    
    // Bir sonraki atama yok → Bu son atama (aktif görev)
    // Sadece bu kullanıcıya atanmışsa göster
    if (task.assignedTo === assignment.userId) {
      // Başlangıç zamanı belirle
      if (isFirst && task.firstStartedAt) {
        startTime = new Date(task.firstStartedAt);
      } else {
        startTime = new Date(assignment.assignedAt);
      }
      
      // Görev işlemdeyse veya incelemede → Canlı süre
      // Beklemede ise süre gösterme (firstStartedAt null olur)
      if (task.status === 'in_progress' || task.status === 'işlemde' || 
          task.status === 'in_review' || task.status === 'incelemede') {
        // Sadece firstStartedAt varsa süre göster
        if (task.firstStartedAt) {
          endTime = new Date();
          const totalMilliseconds = endTime - startTime;
          return formatDuration(totalMilliseconds);
        }
      }
    }
    
    return null;
  };

  // Calculate total time spent for a specific user (belirli bir kullanıcı için toplam süre)
  const calculateUserTotalDuration = (userId) => {
    if (!task.assignmentHistory || task.assignmentHistory.length === 0) {
      return null;
    }

    // O kullanıcıya ait tüm atamaları bul
    const userAssignments = task.assignmentHistory.filter(a => a.userId === userId);
    
    if (userAssignments.length === 0) {
      return null;
    }

    let totalMilliseconds = 0;
    
    userAssignments.forEach((assignment, index) => {
      // Bu assignment'ın assignment history içindeki global index'ini bul
      const globalIndex = task.assignmentHistory.findIndex(
        a => a.userId === assignment.userId && a.assignedAt === assignment.assignedAt
      );
      
      // Bir sonraki assignment var mı?
      const nextAssignment = task.assignmentHistory[globalIndex + 1];
      
      let startTime;
      let endTime;
      
      // İlk atama mı ve firstStartedAt var mı?
      const isFirstInHistory = globalIndex === 0;
      
      // Başlangıç zamanı
      if (isFirstInHistory && task.firstStartedAt) {
        startTime = new Date(task.firstStartedAt);
      } else {
        startTime = new Date(assignment.assignedAt);
      }
      
      // Bitiş zamanı
      if (assignment.completedAt) {
        // completedAt varsa onu kullan
        endTime = new Date(assignment.completedAt);
      } else if (nextAssignment) {
        // Bir sonraki atama varsa, o atama zamanına kadar
        endTime = new Date(nextAssignment.assignedAt);
      } else if (task.assignedTo === assignment.userId) {
        // Şu anda bu kullanıcıya atanmışsa
        // Sadece firstStartedAt varsa (işlemde/incelemede) süreyi hesapla
        if (task.firstStartedAt && 
            (task.status === 'işlemde' || task.status === 'in_progress' ||
             task.status === 'incelemede' || task.status === 'in_review')) {
          endTime = new Date();
        } else {
          // Beklemedeyse veya firstStartedAt yoksa süreyi ekleme
          return;
        }
      } else {
        // Hiçbiri değilse süreyi ekleme
        return;
      }
      
      totalMilliseconds += (endTime - startTime);
    });

    if (totalMilliseconds === 0) {
      return null;
    }

    return formatDuration(totalMilliseconds);
  };

  // Handle clicking on a change item
  const handleChangeClick = (change) => {
    console.log('🖱️ Change clicked:', change.type, change.changeType);
    
    if (change.type === 'comment' || (change.type === 'history' && change.changeType === 'comment')) {
      // Scroll to comments section - end pozisyonunda yorumlar bölümü tam görünür
      if (commentsRef.current) {
        commentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        // 200ms bekleyip biraz daha aşağı scroll
        setTimeout(() => {
          window.scrollBy({ top: 100, behavior: 'smooth' });
        }, 300);
      }
    } else if (change.type === 'file' || (change.type === 'history' && change.changeType === 'file')) {
      // Scroll to files section - end pozisyonunda dosyalar bölümü tam görünür
      if (filesRef.current) {
        filesRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        setTimeout(() => {
          window.scrollBy({ top: 100, behavior: 'smooth' });
        }, 300);
      }
    } else if (change.type === 'history') {
      // History değişiklikleri için scroll
      const changeType = change.changeType;
      
      if (changeType === 'status') {
        // Durum değişikliği - status bölümüne scroll
        if (statusRef.current) {
          statusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else if (changeType === 'priority') {
        // Öncelik değişikliği - details bölümüne scroll
        if (detailsRef.current) {
          detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else if (changeType === 'assignment') {
        // Atama değişikliği - assignment bölümüne scroll
        if (assignmentRef.current) {
          assignmentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else if (changeType === 'workflow_step') {
        // Workflow adımı - workflow bölümüne scroll
        if (workflowRef.current) {
          workflowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        // Diğer değişiklikler - genel details bölümüne scroll
        if (detailsRef.current) {
          detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  };


  if (!task) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 lg:p-6 space-y-4 w-full overflow-hidden" data-testid="task-detail-page">
      <div className="flex items-center justify-between pt-2 pb-2 sm:pt-3 sm:pb-3 lg:pt-4 lg:pb-4">
        {!isDrawer && (
          <Link to="/tasks" className="inline-flex items-center text-cyan-600 hover:text-cyan-700 text-sm sm:text-base">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Görevlere Dön
          </Link>
        )}
        <div className="flex items-center gap-2">
          {canDeleteTask() && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setShowDeleteDialog(true);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Görevi Sil
            </Button>
          )}
          <Button
            data-testid="task-share-btn"
            size="sm"
            onClick={handleOpenShareModal}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md hover:shadow-lg"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Paylaş
          </Button>
          <Button
            data-testid="task-copy-link-btn"
            size="sm"
            variant="outline"
            onClick={() => {
              const link = `${window.location.origin}/tasks?openTask=${taskId}`;
              navigator.clipboard.writeText(link);
              toast.success('Link kopyalandı');
            }}
            className="px-2"
            title="Paylaşım linkini kopyala"
          >
            <Link2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col xl:grid xl:grid-cols-3 gap-4">
        {/* Main Content */}
        <div className="xl:col-span-2 space-y-4 min-w-0">
          <div 
            ref={detailsRef}
            className={`glass rounded-2xl p-6 transition-all duration-500 ${
              highlightSection === 'details' || highlightSection === 'status' || highlightSection === 'assignment' ? 'ring-4 ring-yellow-400 bg-yellow-50' : ''
            }`}
          >
            {project && (
              <div className="text-sm text-gray-700 font-medium mb-1 flex items-center gap-1">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                {project.name}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2">
                  <h1 className="text-2xl font-bold text-gray-900 mb-1 break-all flex-1 min-w-0">{task.title}</h1>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        if (navigator.clipboard?.writeText) {
                          await navigator.clipboard.writeText(task.title);
                        } else {
                          // fallback (older browsers / non-secure contexts)
                          const ta = document.createElement('textarea');
                          ta.value = task.title;
                          ta.style.position = 'fixed';
                          ta.style.opacity = '0';
                          document.body.appendChild(ta);
                          ta.select();
                          document.execCommand('copy');
                          document.body.removeChild(ta);
                        }
                        toast.success('Görev adı kopyalandı');
                      } catch (e) {
                        toast.error('Kopyalanamadı');
                      }
                    }}
                    className="mt-1.5 inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors flex-shrink-0"
                    title="Görev adını kopyala"
                    data-testid="copy-task-title-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                {canEdit() && (
                  <button
                    onClick={handleEditTask}
                    className="inline-flex items-center text-gray-400 hover:text-cyan-600 transition-colors text-sm"
                    title="Görevi Düzenle"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Düzenle
                  </button>
                )}
              </div>
              {/* Durum Badge - Büyük ekranlarda görünür */}
              <div className="flex-shrink-0 hidden lg:block relative" data-status-dropdown>
                <div 
                  data-testid="status-badge-desktop"
                  className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border-2 max-w-full${isAdmin ? ' cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                  style={{ 
                    backgroundColor: `${statusHexColors[task.status]}20`, 
                    borderColor: statusHexColors[task.status],
                    color: statusHexColors[task.status]
                  }}
                  onClick={isAdmin ? () => setShowStatusDropdown(!showStatusDropdown) : undefined}
                >
                  <span className="truncate">{statusLabels[task.status]}</span>
                  {isAdmin && <ChevronDown className="w-3.5 h-3.5 ml-1 flex-shrink-0" />}
                </div>
                {isAdmin && showStatusDropdown && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[160px]" data-testid="status-dropdown">
                    {['beklemede', 'işlemde', 'incelemede', 'tamamlandı'].map((s) => (
                      <button
                        key={s}
                        data-testid={`status-option-${s}`}
                        onClick={() => handleStatusChange(s)}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${task.status === s ? 'font-semibold bg-gray-50' : ''}`}
                      >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusHexColors[s] }} />
                        {statusLabels[s]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {task.description && (
              <div
                className="text-gray-600 mb-3 text-sm"
                data-testid="task-description-rendered"
                dangerouslySetInnerHTML={formatComment(task.description)}
              />
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-sm">
              <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                {/* Öncelik Badge - Tıklanabilir (admin/proje_yoneticisi) */}
                <div className="relative" data-priority-dropdown>
                  <div 
                    data-testid="priority-badge"
                    className={`inline-flex items-center px-2 py-1 rounded-md font-semibold border-2 text-xs max-w-full${canChangePriority ? ' cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                    style={{ 
                      backgroundColor: `${priorityHexColors[task.priority]}20`, 
                      borderColor: priorityHexColors[task.priority],
                      color: priorityHexColors[task.priority]
                    }}
                    onClick={canChangePriority ? () => setShowPriorityDropdown(!showPriorityDropdown) : undefined}
                  >
                    <span className="truncate">Öncelik: {priorityLabels[task.priority]}</span>
                    {canChangePriority && <ChevronDown className="w-3 h-3 ml-1 flex-shrink-0" />}
                  </div>
                  {canChangePriority && showPriorityDropdown && (
                    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[140px]" data-testid="priority-dropdown">
                      {['urgent', 'high', 'medium', 'low'].map((p) => (
                        <button
                          key={p}
                          data-testid={`priority-option-${p}`}
                          onClick={() => handlePriorityChange(p)}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${task.priority === p ? 'font-semibold bg-gray-50' : ''}`}
                        >
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: priorityHexColors[p] }} />
                          {priorityLabels[p]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Durum Badge - Küçük ekranlarda görünür */}
                <div className="relative lg:hidden ml-auto" data-status-dropdown>
                  <div 
                    data-testid="status-badge-mobile"
                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border-2 max-w-full${isAdmin ? ' cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                    style={{ 
                      backgroundColor: `${statusHexColors[task.status]}20`, 
                      borderColor: statusHexColors[task.status],
                      color: statusHexColors[task.status]
                    }}
                    onClick={isAdmin ? () => setShowStatusDropdown(!showStatusDropdown) : undefined}
                  >
                    <span className="truncate">{statusLabels[task.status]}</span>
                    {isAdmin && <ChevronDown className="w-3.5 h-3.5 ml-1 flex-shrink-0" />}
                  </div>
                  {isAdmin && showStatusDropdown && (
                    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[160px]" data-testid="status-dropdown-mobile">
                      {['beklemede', 'işlemde', 'incelemede', 'tamamlandı'].map((s) => (
                        <button
                          key={s}
                          data-testid={`status-option-mobile-${s}`}
                          onClick={() => handleStatusChange(s)}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 transition-colors ${task.status === s ? 'font-semibold bg-gray-50' : ''}`}
                        >
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusHexColors[s] }} />
                          {statusLabels[s]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {task.deadline && (
                  <span className="text-gray-600">
                    Son Tarih: {new Date(task.deadline).toLocaleDateString('tr-TR')}
                  </span>
                )}
              </div>
              {/* Kategori + Görüntülenme Sayısı (yan yana grup) */}
              {(task.tagId || viewCount > 0) && (
                <div className="flex flex-row items-center gap-2 w-full sm:w-auto">
                  {/* Kategori Bilgisi (mobilde kalan alanı doldurur) */}
                  {task.tagId && (() => {
                    const taskTag = tags.find(t => t.id === task.tagId);
                    return taskTag ? (
                      <div
                        className="inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-medium border-2 flex-1 sm:flex-initial min-w-0"
                        style={{
                          backgroundColor: `${taskTag.color}20`,
                          borderColor: taskTag.color,
                          color: taskTag.color
                        }}
                      >
                        <svg className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
                        </svg>
                        <span className="truncate">{taskTag.name}</span>
                      </div>
                    ) : null;
                  })()}
                  {/* Görüntülenme Sayısı (Göz İkonu Badge) — kendi boyutunu korur */}
                  {viewCount > 0 && (
                    <div
                      data-testid="task-view-count-badge"
                      className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border border-gray-300 bg-gray-50 text-gray-700 flex-shrink-0"
                      title={`Toplam görüntülenme: ${viewCount}${userViewCount ? ` • Senin görüntülemen: ${userViewCount}` : ''}`}
                    >
                      <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {viewCount}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Miktar Bilgisi - BELİRGİN */}
            {task.quantityType && task.quantityValue && (
              <div className="mt-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-300 rounded-lg p-3 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="bg-purple-600 text-white rounded-lg p-2">
                      {task.quantityType === 'süre' ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="text-xs font-medium text-purple-600 mb-0.5">
                        Görev Miktarı
                      </div>
                      <div className="flex items-baseline space-x-1.5">
                        <span className="text-lg font-bold text-purple-900">
                          {task.quantityValue}
                        </span>
                        <span className="text-sm font-medium text-purple-700">
                          {task.quantityType === 'süre' ? '(Süre)' : '(Adet)'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Düzenle butonu sağ üstte */}
                  {canEditQuantity() && (
                    <div className="flex-shrink-0">
                      <button
                        onClick={handleEditQuantity}
                        className="bg-purple-600 hover:bg-purple-700 text-white rounded-lg px-3 py-1.5 flex items-center space-x-1.5 transition-all shadow-sm hover:shadow-md"
                        title="Miktar Değerini Düzenle"
                      >
                        <Edit className="w-4 h-4" />
                        <span className="text-sm font-medium">Düzenle</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Detaylar - 2x2 Grid Kompakt */}
            <div className="mt-2 glass rounded-xl p-3 border border-gray-200">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                  <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-500 text-[10px]">Oluşturan</p>
                    <p className="font-medium text-gray-900 text-xs truncate">{getUserName(task.createdBy)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                  <svg className="w-4 h-4 text-cyan-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-500 text-[10px]">Oluşturma</p>
                    <p className="font-medium text-gray-900 text-xs">{new Date(task.createdAt).toLocaleDateString('tr-TR')} {new Date(task.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                  <svg className="w-4 h-4 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-500 text-[10px]">Güncelleme</p>
                    <p className="font-medium text-gray-900 text-xs">{new Date(task.updatedAt).toLocaleDateString('tr-TR')} {new Date(task.updatedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
                {task.assignedTo && (
                  <div className="flex items-center gap-2 p-2 bg-white rounded-lg">
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-500 text-[10px]">Son Atanan</p>
                      <p className="font-medium text-gray-900 text-xs truncate">{getUserName(task.assignedTo)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Workflow Progress */}
          {workflow && (
            <div 
              ref={workflowRef} 
              className={`glass rounded-2xl p-4 transition-all duration-500 ${
                highlightSection === 'workflow' ? 'ring-4 ring-yellow-400 bg-yellow-50' : ''
              }`}
            >
              <h3 className="font-semibold text-gray-900 mb-4">İş Akışı: {workflow.name}</h3>
              <div className="space-y-3">
                {workflow.steps.map((step, index) => {
                  const checkoutRecord = task.checkoutHistory?.find(h => h.stepIndex === index);
                  const isTaskCompleted = task.status === 'tamamlandı' || task.status === 'completed';
                  
                  // Mevcut adım kontrolü - Revizyon durumunu da göz önünde bulundur
                  // Eğer currentStepIndex negatifse (revizyon), assignmentHistory'den son aktif adımı bul
                  let actualCurrentStepIndex = task.currentStepIndex;
                  if (task.currentStepIndex < 0) {
                    // Revizyon durumunda, assignmentHistory'deki son completedAt = null VE stepIndex >= 0 olan kaydın stepIndex'i
                    // Revizyon kayıtlarını (stepIndex < 0) atla
                    const activeAssignment = task.assignmentHistory?.find(a => !a.completedAt && a.stepIndex >= 0);
                    if (activeAssignment) {
                      actualCurrentStepIndex = activeAssignment.stepIndex;
                      
                      // DEBUG - Son test
                      if (index === 0) {
                        console.log('🔍 FINAL DEBUG:', {
                          activeAssignment,
                          actualCurrentStepIndex,
                          'index === actualCurrentStepIndex': index === actualCurrentStepIndex,
                          isTaskCompleted
                        });
                      }
                    } else {
                      console.log('⚠️ Active assignment bulunamadı!', task.assignmentHistory);
                    }
                  }
                  
                  // Mevcut adım sadece görev tamamlanmamışsa ve adım indexi actualCurrentStepIndex'e eşitse
                  const isCurrentStep = index === actualCurrentStepIndex && !isTaskCompleted;
                  
                  // DÜZELTME: Sadece gerçekten tamamlanmış adımları yeşil göster
                  // Ama mevcut adımsa tamamlanmış sayma (revizyon durumu için)
                  // assignmentHistory'de stepIndex ile eşleşen ve completedAt olan kayıtları kontrol et
                  const hasCompletedAssignment = task.assignmentHistory?.some(assignment => 
                    assignment.stepIndex === index && assignment.completedAt
                  );
                  
                  // checkoutHistory'de de kontrol et (eski görevler için)
                  const hasCheckoutRecord = checkoutRecord !== undefined;
                  
                  // Son adım ve görev tamamlandıysa, son adımı da tamamlanmış say
                  const isLastStep = index === workflow.steps.length - 1;
                  
                  // ÖNEMLİ: Mevcut adımsa tamamlanmış sayma (revizyon durumu için)
                  const isStepCompleted = !isCurrentStep && (hasCompletedAssignment || hasCheckoutRecord || (isTaskCompleted && index <= task.currentStepIndex));
                  
                  // Adıma atanan kişi/takım ismini bul
                  let assignedName = '';
                  let isManualOverride = false;
                  let actualUserId = null;
                  
                  // ÖNCELİK 1: assignmentHistory'deki bu adım için SON atamayı al
                  // (tamamlanmış veya tamamlanmamış fark etmez - en güncel atama)
                  const stepAssignments = task.assignmentHistory?.filter(a => 
                    a.stepIndex === index
                  ) || [];
                  const lastAssignment = stepAssignments.length > 0 
                    ? stepAssignments[stepAssignments.length - 1] 
                    : null;
                  
                  if (lastAssignment && lastAssignment.userId) {
                    actualUserId = lastAssignment.userId;
                    const realUser = users.find(u => u.id === actualUserId);
                    assignedName = realUser ? realUser.name : 'Bilinmeyen';
                    isManualOverride = (actualUserId !== step.assignToId);
                  } else if (task.manualOverrides && task.manualOverrides[index.toString()]) {
                    // ÖNCELİK 2: Manuel override kontrolü (henüz assignmentHistory kaydı yoksa)
                    actualUserId = task.manualOverrides[index.toString()];
                    const manualUser = users.find(u => u.id === actualUserId);
                    assignedName = manualUser ? manualUser.name : 'Bilinmeyen';
                    isManualOverride = true;
                  } else if (step.assignToType === 'user' && step.assignToId) {
                    // ÖNCELİK 3: Workflow definition'dan default atamayı göster
                    actualUserId = step.assignToId;
                    const assignedUser = users.find(u => u.id === actualUserId);
                    assignedName = assignedUser ? assignedUser.name : 'Bilinmeyen';
                  } else if (step.assignToType === 'team' && step.assignToId) {
                    // ÖNCELİK 4: Takım ataması
                    const assignedTeam = teams.find(t => t.id === step.assignToId);
                    assignedName = assignedTeam ? assignedTeam.name : 'Bilinmeyen Takım';
                  }
                  
                  return (
                    <div
                      key={index}
                      className={`flex items-center space-x-3 p-3 rounded-lg ${
                        isCurrentStep
                          ? 'bg-blue-100 border border-blue-200'
                          : isStepCompleted
                          ? 'bg-green-50 border border-green-300'
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isCurrentStep
                            ? 'bg-blue-500'
                            : isStepCompleted
                            ? 'bg-green-500'
                            : 'bg-gray-300'
                        }`}
                      >
                        {isCurrentStep ? (
                          <span className="text-white font-semibold text-sm">{index + 1}</span>
                        ) : isStepCompleted ? (
                          <CheckCircle className="w-5 h-5 text-white" />
                        ) : (
                          <span className="text-white font-semibold text-sm">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium flex-1 ${isCurrentStep ? 'text-blue-700' : 'text-gray-900'}`}>{step.stepName}</p>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-md flex-shrink-0 ${
                            step.stepType === 'Kontrol' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {step.stepType || 'Üretim'}
                          </span>
                          {assignedName && (
                            (() => {
                              const avatarColors = [
                                { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-200' },
                                { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200' },
                                { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
                                { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
                                { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
                                { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200' },
                                { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700', border: 'border-fuchsia-200' },
                                { bg: 'bg-lime-100', text: 'text-lime-700', border: 'border-lime-200' },
                              ];
                              const cIdx = (actualUserId || '').charCodeAt(0) % avatarColors.length;
                              const userColor = avatarColors[cIdx];
                              return (
                                <span className={`text-xs px-2 py-0.5 rounded-md font-medium flex-shrink-0 flex items-center gap-1 ${userColor.bg} ${userColor.text} border ${userColor.border}`}>
                                  <Users className="w-3 h-3" />
                                  {assignedName}
                                </span>
                              );
                            })()
                          )}
                          
                          {/* Atamayı Değiştir Butonu - Admin ve Proje Yöneticisi, tüm tamamlanmamış adımlarda */}
                          {(user?.role === 'admin' || user?.role === 'proje_yoneticisi') && !isStepCompleted && (
                            <button
                              onClick={() => {
                                // Bu adımdaki açık assignment'ı bul
                                const openAssignment = task.assignmentHistory?.find(a => 
                                  a.stepIndex === index && !a.completedAt
                                );
                                const assignmentIdx = task.assignmentHistory?.findIndex(a => 
                                  a.stepIndex === index && !a.completedAt
                                );
                                
                                // Aynı assignmentHistory öncelik mantığı
                                let actualUserId;
                                let actualUserName;
                                
                                // ÖNCELİK 1: assignmentHistory'deki bu adım için son atama
                                const reassignStepAssignments = task.assignmentHistory?.filter(a => 
                                  a.stepIndex === index
                                ) || [];
                                const reassignLastAssignment = reassignStepAssignments.length > 0 
                                  ? reassignStepAssignments[reassignStepAssignments.length - 1] 
                                  : null;
                                
                                if (reassignLastAssignment) {
                                  actualUserId = reassignLastAssignment.userId;
                                  const realUser = users.find(u => u.id === actualUserId);
                                  actualUserName = realUser?.name || 'Bilinmeyen';
                                } else if (task.manualOverrides && task.manualOverrides[index.toString()]) {
                                  // ÖNCELİK 2: Manuel override
                                  actualUserId = task.manualOverrides[index.toString()];
                                  const realUser = users.find(u => u.id === actualUserId);
                                  actualUserName = realUser?.name || 'Bilinmeyen';
                                } else {
                                  // ÖNCELİK 3: Workflow varsayılanı
                                  actualUserId = step.assignToId;
                                  actualUserName = assignedName || 'Bilinmeyen';
                                }
                                
                                setReassignData({
                                  newUserId: '',
                                  reason: '',
                                  assignmentIndex: assignmentIdx >= 0 ? assignmentIdx : null,
                                  oldUserId: actualUserId,
                                  oldUserName: actualUserName,
                                  stepIndex: index  // KRİTİK: Hangi adımın değiştirildiği bilgisi
                                });
                                setShowReassignDialog(true);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full p-1.5 transition-colors"
                              title="Atamayı Değiştir"
                            >
                              <UserPlus className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        {/* Kategori ve Miktar Bilgileri */}
                        {(() => {
                          // Bu adımdaki tamamlanmış assignment'ı bul
                          const completedAssignment = task.assignmentHistory?.find(a => 
                            a.stepIndex === index && a.completedAt
                          );
                          
                          if (completedAssignment && (completedAssignment.completedTagId || completedAssignment.completedQuantityValue)) {
                            const tag = tags.find(t => t.id === completedAssignment.completedTagId);
                            const canEdit = user.role === 'admin' || completedAssignment.userId === user.id;
                            
                            return (
                              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200">
                                {tag && (
                                  <span 
                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                                    style={{
                                      backgroundColor: `${tag.color}20`,
                                      color: tag.color
                                    }}
                                  >
                                    <FolderOpen className="w-3 h-3 mr-1" />
                                    {tag.name}
                                  </span>
                                )}
                                {completedAssignment.completedQuantityValue && (
                                  <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-xs font-medium">
                                    <Layers className="w-3 h-3 mr-1" />
                                    {completedAssignment.completedQuantityValue}
                                    {completedAssignment.completedQuantityType === 'adet' ? ' adet' : ''}
                                  </span>
                                )}
                                {canEdit && (
                                  <button
                                    onClick={() => {
                                      const assignmentIndex = task.assignmentHistory?.findIndex(a => 
                                        a.stepIndex === index && a.completedAt
                                      );
                                      setEditStepQuantityData({
                                        stepIndex: index,
                                        assignmentIndex,
                                        tagId: completedAssignment.completedTagId || '',
                                        quantityType: completedAssignment.completedQuantityType || 'adet',
                                        quantityValue: completedAssignment.completedQuantityValue || ''
                                      });
                                      setShowEditStepQuantityDialog(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-full p-1 transition-colors"
                                    title="Düzenle"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <p className="text-xs text-gray-500">
                            {isCurrentStep
                              ? 'Mevcut adım' 
                              : isStepCompleted
                              ? 'Tamamlandı' 
                              : 'Beklemede'}
                          </p>
                          {calculateStepDuration(index) && (
                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-md font-medium flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              {calculateStepDuration(index)}
                            </span>
                          )}
                        </div>
                        {checkoutRecord && (
                          <p className="text-xs text-green-600 mt-1 flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {checkoutRecord.userName} - {new Date(checkoutRecord.checkoutAt).toLocaleString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ADIM LİSTESİ - Direkt Atama Görevleri İçin */}
          {!task.workflowId && stepQuantities && stepQuantities.length > 0 && (
            <div className="glass rounded-2xl p-6">
              <h3 className="font-semibold text-gray-900 mb-4">ADIM LİSTESİ</h3>
              <div className="space-y-3">
                {stepQuantities.map((step, index) => {
                  // Orijinal kullanıcıyı her zaman kullan (revizyon döngüsü adım gösterimini değiştirmemeli)
                  const actualUserId = step.userId;
                  const assignedUser = users.find(u => u.id === actualUserId);
                  const tag = tags.find(t => t.id === step.tagId);
                  
                  // Bu kullanıcı görevin şu an atandığı kişi mi?
                  const isAssignedNow = actualUserId === task.assignedTo;
                  
                  // Bu kullanıcının HERHANGİ BİR açık assignment'ı var mı?
                  const hasOpenAssignment = task.assignmentHistory?.some(a => 
                    a.userId === actualUserId && !a.completedAt
                  );
                  
                  // Bu kullanıcının TÜM assignment'ları tamamlanmış mı?
                  const allAssignmentsCompleted = task.assignmentHistory?.some(a => 
                    a.userId === actualUserId && a.completedAt
                  );
                  
                  // Bu kullanıcının son tamamlanmış assignment'ını bul (stepType için)
                  const completedAssignment = task.assignmentHistory?.find(a => 
                    a.userId === actualUserId && a.completedAt
                  );
                  
                  // Mevcut adım: görevin atandığı kişi BU adımın orijinal kullanıcısı ise
                  const isCurrentStep = isAssignedNow && hasOpenAssignment && task.status !== 'tamamlandı';
                  const isStepCompleted = allAssignmentsCompleted && !isCurrentStep;
                  
                  return (
                    <div
                      key={index}
                      className={`flex items-center space-x-3 p-3 rounded-lg ${
                        isCurrentStep
                          ? 'bg-blue-100 border border-blue-200'
                          : isStepCompleted
                          ? 'bg-green-50 border border-green-300'
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isCurrentStep
                            ? 'bg-blue-500'
                            : isStepCompleted
                            ? 'bg-green-500'
                            : 'bg-gray-300'
                        }`}
                      >
                        {isCurrentStep ? (
                          <span className="text-white font-semibold text-sm">{index + 1}</span>
                        ) : isStepCompleted ? (
                          <CheckCircle className="w-5 h-5 text-white" />
                        ) : (
                          <span className="text-white font-semibold text-sm">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium flex-1 ${isCurrentStep ? 'text-blue-700' : 'text-gray-900'}`}>{step.stepName || 'Adım'}</p>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-md flex-shrink-0 ${
                            step.stepType === 'Kontrol' || step.stepName === 'Kontrol' || (completedAssignment && completedAssignment.stepType === 'Kontrol')
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {step.stepType === 'Kontrol' || step.stepName === 'Kontrol' || (completedAssignment && completedAssignment.stepType === 'Kontrol') ? 'Kontrol' : 'Üretim'}
                          </span>
                          {assignedUser && (
                            <span className="text-xs px-2 py-0.5 rounded-md font-medium flex-shrink-0 flex items-center gap-1 bg-gray-100 text-gray-700">
                              <Users className="w-3 h-3" />
                              {assignedUser.name}
                            </span>
                          )}
                          
                          {/* Atamayı Değiştir Butonu - Sadece admin, sadece tamamlanmamış adımlarda (Direkt Atama için) */}
                          {user?.role === 'admin' && !isStepCompleted && (
                            <button
                              onClick={() => {
                                // Bu direkt atama adımındaki açık assignment'ı bul (stepIndex bazında)
                                const openAssignment = task.assignmentHistory?.find(a => 
                                  a.stepIndex === index && !a.completedAt
                                );
                                const assignmentIdx = task.assignmentHistory?.findIndex(a => 
                                  a.stepIndex === index && !a.completedAt
                                );
                                
                                console.log('[REASSIGN-DEBUG-DIRECT] Step:', index, 'OpenAssignment:', openAssignment);
                                console.log('[REASSIGN-DEBUG-DIRECT] ActualUserId:', actualUserId, 'AssignedUser:', assignedUser?.name);
                                console.log('[REASSIGN-DEBUG-DIRECT] Setting stepIndex:', index);
                                
                                setReassignData({
                                  newUserId: '',
                                  reason: '',
                                  assignmentIndex: assignmentIdx >= 0 ? assignmentIdx : null,
                                  oldUserId: actualUserId,
                                  oldUserName: assignedUser?.name || 'Bilinmeyen',
                                  stepIndex: index  // Direkt atama için de stepIndex ekle
                                });
                                setShowReassignDialog(true);
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full p-1.5 transition-colors"
                              title="Atamayı Değiştir"
                            >
                              <UserPlus className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        {/* Kategori ve Miktar Bilgileri */}
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200">
                          {tag && (
                            <span 
                              className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                              style={{
                                backgroundColor: `${tag.color}20`,
                                color: tag.color
                              }}
                            >
                              <FolderOpen className="w-3 h-3 mr-1" />
                              {tag.name}
                            </span>
                          )}
                          {step.quantityValue && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md text-xs font-medium">
                              <Layers className="w-3 h-3 mr-1" />
                              {step.quantityValue}
                              {step.quantityType === 'adet' ? ' adet' : ''}
                            </span>
                          )}
                          {/* Düzenle butonu kaldırıldı - Görev bilgilerinden miktar güncellenebiliyor */}
                        </div>
                        
                        <div className="flex items-center justify-between gap-2 mt-2">
                          <p className="text-xs text-gray-500">
                            {isCurrentStep
                              ? 'Mevcut adım' 
                              : isStepCompleted
                              ? 'Tamamlandı' 
                              : 'Beklemede'}
                          </p>
                          {(() => {
                            // Bu kullanıcının tüm assignment kayıtlarını bul ve toplam süreyi hesapla
                            const userAssignments = task.assignmentHistory?.filter(a => a.userId === step.userId) || [];
                            if (userAssignments.length === 0 || !task.firstStartedAt) return null;
                            
                            let totalMilliseconds = 0;
                            userAssignments.forEach((assignment, idx) => {
                              let startTime;
                              if (idx === 0 && userAssignments[0].stepIndex === 0) {
                                startTime = new Date(task.firstStartedAt);
                              } else {
                                startTime = new Date(assignment.assignedAt);
                              }
                              
                              let endTime;
                              if (assignment.completedAt) {
                                endTime = new Date(assignment.completedAt);
                              } else {
                                endTime = new Date();
                              }
                              
                              totalMilliseconds += (endTime - startTime);
                            });
                            
                            if (totalMilliseconds === 0) return null;
                            
                            const duration = formatDuration(totalMilliseconds);
                            return duration ? (
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-md font-medium ml-auto flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                {duration}
                              </span>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Assignment History */}
          {task.assignmentHistory && task.assignmentHistory.length > 0 && (
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Users className="w-6 h-6 text-purple-600" />
                <h3 className="font-semibold text-gray-900">Görev Geçmişi ({task.assignmentHistory.length})</h3>
              </div>
              <div className="space-y-3">
                {task.assignmentHistory.map((assignment, index) => {
                  const assignedUser = users.find(u => u.id === assignment.userId);
                  const userName = assignedUser?.name || 'Bilinmeyen Kullanıcı';
                  const userPicture = assignedUser?.picture;
                  const isFirstAssignment = index === 0;
                  const duration = calculateAssignmentDuration(assignment, isFirstAssignment, index);
                  const userTotalDuration = calculateUserTotalDuration(assignment.userId);
                  
                  return (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        {(() => {
                          const avatarColors = [
                            'from-violet-400/90 to-purple-500/90',
                            'from-rose-400/90 to-pink-500/90',
                            'from-emerald-400/90 to-teal-500/90',
                            'from-blue-400/90 to-indigo-500/90',
                            'from-amber-400/90 to-orange-500/90',
                            'from-cyan-400/90 to-sky-500/90',
                            'from-fuchsia-400/90 to-pink-500/90',
                            'from-lime-400/90 to-green-500/90',
                          ];
                          const colorIndex = (assignment.userId || '').charCodeAt(0) % avatarColors.length;
                          const gradient = avatarColors[colorIndex];
                          
                          if (assignment.completedAt) {
                            return (
                              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-100 text-green-600 border-2 border-white shadow-sm">
                                <CheckCircle className="w-6 h-6" />
                              </div>
                            );
                          }
                          return (
                            <div className={`w-10 h-10 rounded-full border-2 border-white shadow-sm overflow-hidden ${userPicture ? '' : `bg-gradient-to-br ${gradient}`}`}>
                              {userPicture ? (
                                <img 
                                  src={userPicture.startsWith('http') ? userPicture : `${window.location.origin}${userPicture}`}
                                  alt={userName}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-white font-bold text-sm">
                                  {userName.charAt(0).toUpperCase()}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="mb-2">
                          {/* Desktop: Tek satır düzen, Mobile: İki satır düzen */}
                          <div className="hidden sm:flex sm:items-center sm:justify-between gap-2 mb-2">
                            {/* Desktop - Tek satırda hepsi */}
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {assignedUser ? assignedUser.name : 'Bilinmeyen Kullanıcı'}
                              </p>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {assignment.stepName && (
                                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md font-medium">
                                    {assignment.stepName}
                                  </span>
                                )}
                                {assignment.stepType && (
                                  <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${
                                    assignment.stepType === 'Kontrol' 
                                      ? 'bg-purple-100 text-purple-700' 
                                      : 'bg-green-100 text-green-700'
                                  }`}>
                                    {assignment.stepType}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {userTotalDuration && (
                                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md font-medium">
                                  T: {userTotalDuration}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Mobile: İki satır düzen */}
                          <div className="sm:hidden">
                            {/* Üst satır: Kullanıcı adı ve İnceleme badge'i */}
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <p className="font-medium text-gray-900 truncate flex-1">
                                {assignedUser ? assignedUser.name : 'Bilinmeyen Kullanıcı'}
                              </p>
                              {assignment.stepName && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md font-medium flex-shrink-0">
                                  {assignment.stepName}
                                </span>
                              )}
                            </div>
                            
                            {/* Alt satır: Üretim/Kontrol badge'i ve Toplam süre badge'i sağda */}
                            <div className="flex items-center justify-end gap-2">
                              {assignment.stepType && (
                                <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${
                                  assignment.stepType === 'Kontrol' 
                                    ? 'bg-purple-100 text-purple-700' 
                                    : 'bg-green-100 text-green-700'
                                }`}>
                                  {assignment.stepType}
                                </span>
                              )}
                              {userTotalDuration && (
                                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md font-medium">
                                  T: {userTotalDuration}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="mt-1 space-y-1">
                          <p className="text-xs text-gray-500 flex items-center gap-1">
                            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            Atandı: {new Date(assignment.assignedAt).toLocaleDateString('tr-TR')} - {new Date(assignment.assignedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </p>
                          {assignment.completedAt && (
                            <p className="text-xs text-green-600 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              Tamamlandı: {new Date(assignment.completedAt).toLocaleDateString('tr-TR')} - {new Date(assignment.completedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </p>
                          )}
                          {!assignment.completedAt && task.assignedTo === assignment.userId && (
                            <p className={`text-xs font-medium flex items-center gap-1 ${
                              task.status === 'in_progress' ? 'text-blue-600' : 
                              task.status === 'pending' ? 'text-amber-600' : 
                              task.status === 'review' ? 'text-purple-600' : 
                              task.status === 'completed' ? 'text-green-600' : 
                              'text-gray-600'
                            }`}>
                              {task.status === 'in_progress' ? <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg> Şu anda üzerinde çalışıyor</> : 
                               task.status === 'pending' ? <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Bekliyor</> : 
                               task.status === 'review' ? <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> Gözden Geçiriliyor</> : 
                               task.status === 'completed' ? <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Tamamlandı</> : 
                               <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg> Atanmış</>}
                            </p>
                          )}
                          {duration && (
                            <p className="text-xs font-medium text-purple-600 flex items-center gap-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              Bu atamada: {duration}
                            </p>
                          )}
                          {assignment.reviewNote && (
                            <div className="mt-2 p-2 bg-blue-50 border-l-4 border-blue-400 rounded">
                              <div className="text-xs text-gray-700">
                                <span className="font-semibold text-blue-700 flex items-center gap-1 mb-1"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg> Açıklama:</span>
                                <span dangerouslySetInnerHTML={formatComment(assignment.reviewNote)} />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Bu görev {task.assignmentHistory.length} kişiye atandı
              </p>
            </div>
          )}

          {/* Revizyon Geçmişi */}
          {task.revisionHistory && task.revisionHistory.length > 0 && (
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>
                <h3 className="font-semibold text-gray-900">
                  Revizyon Geçmişi ({task.revisionCount || 0})
                </h3>
              </div>
              <div className="space-y-4">
                {task.revisionHistory.slice().reverse().map((revision, index) => {
                  const isResolved = revision.status === 'resolved';
                  
                  return (
                    <div key={revision.id} className={`p-4 rounded-lg border-2 ${
                      isResolved ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-md flex items-center gap-1 ${
                            isResolved ? 'bg-green-200 text-green-800' : 'bg-orange-200 text-orange-800'
                          }`}>
                            {isResolved ? <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Çözüldü</> : <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> Bekliyor</>}
                          </span>
                          <span className="text-sm text-gray-600">
                            {new Date(revision.sentAt).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })} - {new Date(revision.sentAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center text-sm">
                          <span className="font-medium text-gray-700">Gönderen:</span>
                          <span className="ml-2 text-gray-900">{revision.sentByName}</span>
                        </div>
                        
                        <div className="flex items-center text-sm">
                          <span className="font-medium text-gray-700">Gönderilen:</span>
                          <span className="ml-2 text-gray-900">
                            {revision.sentTo.map(u => u.userName).join(', ')}
                          </span>
                        </div>
                        
                        <div className="mt-2">
                          <p className="font-medium text-gray-700 text-sm mb-1">Revizyon Nedeni:</p>
                          <div className="text-sm text-gray-900 bg-white p-2 rounded border" dangerouslySetInnerHTML={formatComment(revision.reason)} />
                        </div>
                        
                        {revision.files && revision.files.length > 0 && (
                          <div className="mt-2">
                            <p className="font-medium text-gray-700 text-sm mb-1">
                              Eklenen Dosyalar ({revision.files.length}):
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {revision.files.filter(file => file.id || file.fileId).map((file, idx) => (
                                <div key={idx} className="group flex items-center gap-1">
                                  <a
                                    href={`${window.location.origin}/api/files/${file.id || file.fileId}/download?token=${localStorage.getItem('session_token') || ''}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-xs px-3 py-2 bg-white rounded-lg border border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700 transition-all cursor-pointer"
                                    title={`İndir: ${file.filename}`}
                                  >
                                    <span className="flex items-center gap-1">
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg> {file.filename}
                                    </span>
                                    <svg 
                                      className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                  </a>
                                  {(user?.role === 'admin' || user?.permissions?.includes('can_delete_files') || revision.sentBy === user?.id) && (
                                    <button
                                      data-testid={`revision-file-delete-${file.id || file.fileId}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteFile(file.id || file.fileId);
                                      }}
                                      className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all"
                                      title="Dosyayı sil"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {isResolved && revision.resolvedAt && (
                          <div className="mt-2 pt-2 border-t border-green-200">
                            <p className="text-xs text-green-700">
                              ✓ {new Date(revision.resolvedAt).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })} - {new Date(revision.resolvedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })} tarihinde tamamlandı
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Toplam {task.revisionHistory.length} revizyon gönderildi
              </p>
            </div>
          )}

          {/* İnceleme Dialog */}
          <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader className="bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700 -m-6 mb-0 px-6 py-4 rounded-t-lg">
                <DialogTitle className="text-xl font-bold text-white">Kontrol İçin Gönder</DialogTitle>
                <DialogDescription className="text-cyan-100">
                  Görevi inceleme için kime göndermek istersiniz?
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-6 px-1">
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-5 rounded-xl border-2 border-cyan-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-cyan-600" />
                    <span className="text-base font-semibold text-gray-800">İnceleme Yapacak Kişi</span>
                    <span className="text-red-500">*</span>
                  </div>
                  {reviewOptions.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">İnceleme seçenekleri hazırlanıyor...</p>
                  ) : (
                    <div className="space-y-2">
                      {reviewOptions.map((option) => (
                        <label
                          key={option.id}
                          className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                            selectedReviewer === option.id
                              ? 'border-cyan-500 bg-gradient-to-r from-cyan-100 to-blue-100 shadow-md scale-[1.02]'
                              : 'border-gray-200 hover:border-cyan-400 hover:bg-cyan-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="reviewer"
                            value={option.id}
                            checked={selectedReviewer === option.id}
                            onChange={(e) => setSelectedReviewer(e.target.value)}
                            className="w-5 h-5 text-cyan-600"
                          />
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900 flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-600" />
                              {option.name}
                              <span className="text-xs text-gray-500 font-normal ml-1">
                                ({option.role === 'takim_lideri' ? 'Takım Lideri' : 
                                  option.role === 'proje_yoneticisi' ? 'Proje Yöneticisi' : 
                                  option.role === 'admin' ? 'Admin' : option.role})
                              </span>
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Kontrol İçin Açıklama (İsteğe Bağlı) */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 rounded-xl border-2 border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    <span className="text-base font-semibold text-gray-800">
                      Kontrol Açıklaması (İsteğe Bağlı)
                    </span>
                  </div>
                  <textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder="Kontrol yapacak kişiye göndermek istediğiniz not veya açıklamayı buraya yazabilirsiniz..."
                    className="w-full px-4 py-3 bg-white border-2 border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800 resize-none"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    **kalın**  *italik*  ## Başlık  - Madde listesi
                  </p>
                </div>
              </div>
              
              <DialogFooter className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg border-t-2 border-gray-200">
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => setShowReviewDialog(false)}
                  className="px-6 border-2 hover:bg-gray-100"
                >
                  <X className="w-4 h-4 mr-2" />
                  İptal
                </Button>
                <Button 
                  size="lg"
                  onClick={handleSendForReview}
                  disabled={!selectedReviewer || reviewOptions.length === 0}
                  className="px-6 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Gönder
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Revizyon Dialog */}
          {/* Custom Modal for Revision - No Dialog Wrapper */}
          {showRevisionDialog && ReactDOM.createPortal(
            <div className="fixed inset-0 z-[10000] flex items-center justify-center">
              {/* Backdrop */}
              <div 
                className="absolute inset-0 bg-black/15"
                onClick={() => {
                  setShowRevisionDialog(false);
                  setRevisionReason('');
                  setSelectedRevisionUsers([]);
                  setSelectedRevisionSteps([]);
                  setRevisionFiles([]);
                }}
              />
              
              {/* Modal Container */}
              <div className="relative z-[10000] w-full max-w-2xl h-[80vh] mx-4 my-0 bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden">
                {/* Close Button */}
                <button
                  onClick={() => {
                    setShowRevisionDialog(false);
                    setRevisionReason('');
                    setSelectedRevisionUsers([]);
                    setSelectedRevisionSteps([]);
                    setRevisionFiles([]);
                  }}
                  className="absolute right-3 top-3 z-[10001] p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
                >
                  <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
                </button>
                
                {/* Header */}
                <div className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-700 px-6 py-4 rounded-t-lg flex-shrink-0">
                  <h3 className="text-xl font-bold text-white">Görevi Revizyona Gönder</h3>
                  <p className="text-orange-100 text-sm mt-1">
                    Görevi önceki kullanıcılara geri gönderin ve revizyon nedeni belirtin.
                  </p>
                </div>
                
                {/* Scrollable content area */}
                <div className="overflow-y-auto flex-1 p-6 bg-gradient-to-br from-gray-50 to-orange-50/30">
                <form onSubmit={handleSendRevision} className="space-y-4">
                  {/* Revizyon Nedeni */}
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 p-4 rounded-xl border-2 border-orange-200">
                    <Label htmlFor="revision-reason" className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
                      <MessageSquare className="w-4 h-4 text-orange-600" />
                      Revizyon Nedeni <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="revision-reason"
                      value={revisionReason}
                      onChange={(e) => setRevisionReason(e.target.value)}
                      placeholder="Lütfen detaylı bir açıklama yapın..."
                      className="bg-white border-2 border-orange-300 focus:border-orange-500 focus:ring-orange-500 min-h-[100px]"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      **kalın**  *italik*  ## Başlık  - Madde listesi
                    </p>
                  </div>
                  
                  {/* Dosya Yükleme - Kompakt */}
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl border-2 border-blue-200">
                    <Label className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
                      <Paperclip className="w-4 h-4 text-cyan-600" />
                      Dosya Ekle (Opsiyonel)
                    </Label>
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setShowRevisionFileSelector(true);
                      }}
                      className="w-full bg-white hover:bg-blue-50 border-2 border-blue-300 hover:border-cyan-500 text-cyan-700 font-semibold"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Dosya Ekle veya Seç
                    </Button>
                    {revisionFiles.length > 0 && (
                      <div className="mt-3 p-3 bg-white border-2 border-green-300 rounded-xl">
                        <p className="text-sm text-green-800 font-semibold mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          Seçilen Dosyalar ({revisionFiles.length}):
                        </p>
                        <div className="space-y-1">
                          {Array.from(revisionFiles).map((file, idx) => (
                            <div key={idx} className="flex items-center bg-green-50 p-2 rounded-lg text-sm text-green-800">
                              <FileText className="w-4 h-4 mr-2 text-green-600" />
                              <span className="flex-1 truncate font-medium">{file.name}</span>
                              <span className="ml-2 text-green-600 font-semibold text-xs bg-green-100 px-2 py-1 rounded">
                                {(file.size / 1024).toFixed(1)} KB
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Kullanıcı Seçimi - Kompakt */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border-2 border-purple-200">
                    <Label className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
                      <User className="w-4 h-4 text-purple-600" />
                      Gönderilecek Kullanıcı <span className="text-red-500">*</span>
                    </Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto bg-white border-2 border-purple-300 rounded-xl p-3">
                      {task && task.assignmentHistory && task.assignmentHistory.length > 0 ? (
                        // Sadece Üretim adımlarındaki veya Üretim tipindeki kullanıcıları göster
                        (() => {
                          const uniqueUsers = new Map();
                          const currentStepIndex = task.currentStepIndex;
                          
                          task.assignmentHistory.forEach((history) => {
                            let isProduction = false;
                            let stepIndex = history.stepIndex;
                            
                            // Workflow varsa, workflow'dan adım tipini kontrol et
                            if (workflow && stepIndex !== undefined && stepIndex >= 0 && stepIndex < currentStepIndex && stepIndex < workflow.steps.length) {
                              const step = workflow.steps[stepIndex];
                              const stepType = step.stepType || 'Üretim'; // Default Üretim (eski görevler için)
                              
                              // Sadece Üretim adımlarını dahil et
                              if (stepType === 'Üretim') {
                                isProduction = true;
                              }
                            } 
                            // Workflow yoksa (direkt atama), history'deki stepType'a bak
                            else if (!workflow || stepIndex === -1) {
                              const stepType = history.stepType || 'Üretim'; // Default Üretim
                              
                              // Sadece Üretim tipini dahil et
                              if (stepType === 'Üretim') {
                                isProduction = true;
                              }
                            }
                            
                            // Üretim tipindeyse listeye ekle
                            if (isProduction) {
                              if (!uniqueUsers.has(history.userId)) {
                                uniqueUsers.set(history.userId, {
                                  userId: history.userId,
                                  stepNames: [],
                                  stepIndexes: []
                                });
                              }
                              
                              if (history.stepName) {
                                uniqueUsers.get(history.userId).stepNames.push(history.stepName);
                                uniqueUsers.get(history.userId).stepIndexes.push(stepIndex);
                              }
                            }
                          });
                          
                          // stepIndex'e göre sırala (ilk adım önce)
                          return Array.from(uniqueUsers.values())
                            .sort((a, b) => Math.min(...a.stepIndexes) - Math.min(...b.stepIndexes))
                            .map((userInfo) => {
                            const historyUser = users.find(u => u.id === userInfo.userId);
                            if (!historyUser) return null;
                            
                            return (
                              <label
                                key={userInfo.userId}
                                className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                  selectedRevisionUsers.includes(userInfo.userId)
                                    ? 'border-orange-500 bg-gradient-to-r from-orange-100 to-red-100 shadow-sm'
                                    : 'border-gray-200 hover:border-purple-400 hover:bg-purple-50'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="revisionUser"
                                  checked={selectedRevisionUsers.includes(userInfo.userId)}
                                  onChange={() => {
                                    setSelectedRevisionUsers([userInfo.userId]);
                                    // ✅ FIX: Seçilen üretim adımının kesin stepIndex'ini sakla.
                                    // Bir kullanıcı birden fazla üretim adımındaysa en erken (min) adımı seç.
                                    const chosenStepIndex = (userInfo.stepIndexes && userInfo.stepIndexes.length > 0)
                                      ? Math.min(...userInfo.stepIndexes)
                                      : null;
                                    setSelectedRevisionSteps([{ userId: userInfo.userId, stepIndex: chosenStepIndex }]);
                                  }}
                                  className="w-4 h-4 text-orange-600"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <p className="font-semibold text-gray-900 text-sm truncate">{historyUser.name}</p>
                                    <span className="px-2 py-0.5 text-xs font-medium rounded-md bg-green-100 text-green-700 flex-shrink-0">
                                      Üretim
                                    </span>
                                  </div>
                                  {userInfo.stepNames.length > 0 && (
                                    <p className="text-xs text-gray-600 mt-1 truncate">
                                      {userInfo.stepNames.join(' → ')}
                                    </p>
                                  )}
                                </div>
                              </label>
                            );
                          });
                        })()
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">
                          Görev geçmişinde kullanıcı bulunamadı
                        </p>
                      )}
                    </div>
                  </div>
                </form>
                </div>

                {/* Footer - Sabit buton alanı */}
                <div className="bg-gray-50 px-6 py-4 rounded-b-lg border-t-2 border-gray-200 flex-shrink-0">
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setShowRevisionDialog(false);
                        setRevisionReason('');
                        setSelectedRevisionUsers([]);
                        setSelectedRevisionSteps([]);
                        setRevisionFiles([]);
                      }}
                      className="w-full sm:w-auto sm:px-6 border-2 hover:bg-gray-100 order-2 sm:order-1"
                    >
                      <X className="w-4 h-4 mr-2" />
                      İptal
                    </Button>
                    <Button 
                      type="submit"
                      size="sm"
                      disabled={sendingRevision || !revisionReason.trim() || selectedRevisionUsers.length === 0}
                      onClick={handleSendRevision}
                      className="w-full sm:flex-1 sm:px-6 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      {sendingRevision ? 'Gönderiliyor...' : 'Revizyona Gönder'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Görev Tamamlama Dialog (Direkt Atama için) */}
          {/* Custom Modal for Complete Task - No Dialog Wrapper */}
          {showCompleteDialog && ReactDOM.createPortal(
            <div className="fixed inset-0 z-[10000] flex items-center justify-center">
              {/* Backdrop */}
              <div 
                className="absolute inset-0 bg-black/15"
                onClick={() => {
                  setShowCompleteDialog(false);
                  setSelectedReviewer('');
                }}
              />
              
              {/* Modal Container */}
              <div className="relative z-[10000] w-full max-w-2xl h-[85vh] sm:h-[90vh] mx-4 bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden">
                {/* Close Button */}
                <button
                  onClick={() => {
                    setShowCompleteDialog(false);
                    setSelectedReviewer('');
                  }}
                  className="absolute right-3 top-3 z-[10001] p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
                >
                  <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
                </button>
                
                {/* Header */}
                <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-green-700 px-6 py-4 rounded-t-lg flex-shrink-0">
                  <h3 className="text-xl font-bold text-white">Görev Tamamlama Seçenekleri</h3>
                  <p className="text-green-100 text-sm mt-1">
                    {user?.role === 'takim_uyesi' 
                      ? 'Kontrol için göndermek istediğiniz kişiyi seçin'
                      : 'Kontrol için gönder veya direkt tamamla'
                    }
                  </p>
                </div>
                
                {/* Scrollable content area */}
                <div className="overflow-y-auto flex-1 p-6 bg-gradient-to-br from-gray-50 to-green-50/30">
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border-2 border-green-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-800">
                        {user?.role === 'takim_uyesi' ? 'Kontrol Yapacak Kişi (Zorunlu)' : 'Kontrol Yapacak Kişi (Opsiyonel)'}
                      </span>
                    </div>
                    
                    {/* Kompakt liste tasarımı */}
                    <div className="space-y-2">
                      {reviewOptions && reviewOptions.length > 0 ? (
                        reviewOptions.map((option) => (
                          <div 
                            key={option.id}
                            onClick={() => {
                              // Toggle: Seçili kullanıcıya tekrar tıklanırsa seçimi kaldır
                              if (selectedReviewer === option.id) {
                                setSelectedReviewer('');
                              } else {
                                setSelectedReviewer(option.id);
                              }
                            }}
                            className={`flex items-center gap-2 p-2 rounded-lg border transition-all cursor-pointer ${
                              selectedReviewer === option.id 
                                ? 'bg-green-100 border-green-400 shadow-sm'
                                : 'bg-white border-gray-200 hover:border-green-300 hover:bg-green-50'
                            }`}
                          >
                            <div className={`w-3 h-3 rounded-full border-2 ${
                              selectedReviewer === option.id
                                ? 'bg-green-500 border-green-500'
                                : 'border-gray-300'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{option.name}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {option.role === 'admin' ? 'Admin' : 
                                 option.role === 'proje_yoneticisi' ? 'Proje Yöneticisi' : 
                                 option.role === 'takim_lideri' ? 'Takım Lideri' : option.role}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 px-4">
                          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                          <p className="text-sm font-semibold text-gray-700 mb-2">
                            Kontrol için gönderilebilecek yetkili bulunamadı
                          </p>
                          <p className="text-xs text-gray-500">
                            {user?.role === 'takim_uyesi' 
                              ? 'Takım lideriniz veya proje yöneticiniz sistemde kayıtlı değil. Lütfen yöneticinizle iletişime geçin.'
                              : user?.role === 'takim_lideri'
                              ? 'Bu proje için proje yöneticisi tanımlı değil. Lütfen admin ile iletişime geçin.'
                              : 'Sistemde üst yönetici tanımlı değil.'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Açıklama kutusu - Kompakt */}
                  {selectedReviewer && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border-2 border-blue-200 animate-slideDown">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-semibold text-blue-800">
                          Kontrol Açıklaması (İsteğe Bağlı)
                        </span>
                      </div>
                      <textarea
                        value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        placeholder="Kontrol yapacak kişiye göndermek istediğiniz not veya açıklamayı buraya yazabilirsiniz..."
                        className="w-full px-4 py-3 bg-white border-2 border-blue-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800 resize-none text-sm"
                        rows={2}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        **kalın**  *italik*  ## Başlık  - Madde listesi
                      </p>
                    </div>
                  )}
                </div>
                </div>

                {/* Footer - Sabit buton alanı */}
                <div className="bg-gray-50 px-6 py-4 rounded-b-lg border-t-2 border-gray-200 flex-shrink-0">
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setShowCompleteDialog(false);
                        setSelectedReviewer('');
                      }}
                      className="w-full sm:w-auto sm:px-6 border-2 hover:bg-gray-100 order-3 sm:order-1"
                    >
                      <X className="w-4 h-4 mr-2" />
                      İptal
                    </Button>
                    
                    {/* Takım Üyesi Değilse: Direkt Tamamla Butonu */}
                    {user?.role !== 'takim_uyesi' && (
                      <Button 
                        size="sm"
                        disabled={selectedReviewer}  // Reviewer seçildiyse disable
                        onClick={async () => {
                          try {
                            await axios.patch(`${API}/tasks/${taskId}`, { status: 'completed' });
                            toast.success('Görev tamamlandı');
                            setShowCompleteDialog(false);
                            setSelectedReviewer('');
                            fetchTask();
                          } catch (error) {
                            toast.error('Görev tamamlanamadı');
                          }
                        }}
                        className="w-full sm:flex-1 sm:px-6 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Tamamla
                      </Button>
                    )}
                    
                    {/* Kontrol İçin Gönder Butonu */}
                    <Button 
                      size="sm"
                      onClick={handleSendForReview}
                      disabled={!selectedReviewer || (reviewOptions.length === 0 && user?.role === 'takim_uyesi')}
                      className="w-full sm:flex-1 sm:px-6 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed order-2 sm:order-3"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Gönder
                    </Button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Comments */}
          <div 
            ref={commentsRef} 
            className={`glass rounded-2xl p-6 transition-all duration-500 ${
              highlightSection === 'comments' ? 'ring-4 ring-yellow-400 bg-yellow-50' : ''
            }`}
          >
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2" />
              Yorumlar
            </h3>
            <div className="space-y-4 mb-4">
              {comments.length === 0 ? (
                <p className="text-center text-gray-500 text-sm py-4">Henüz yorum yok</p>
              ) : (
                comments.map((comment) => {
                  const isNew = highlightSection === 'comments' && highlightItemId === comment.id;
                  const commentDate = new Date(comment.createdAt);
                  const isRecent = (Date.now() - commentDate.getTime()) < 24 * 60 * 60 * 1000; // Son 24 saat
                  const isRead = readItems.has(comment.id); // Okundu mu?
                  
                  return (
                    <div 
                      key={comment.id} 
                      id={`comment-${comment.id}`}
                      data-item-id={comment.id}
                      data-item-type="comment"
                      className={`p-4 rounded-xl border border-gray-200 shadow-sm transition-all duration-500 ${
                        isNew ? 'bg-yellow-50 border-yellow-300 shadow-lg ring-2 ring-yellow-400/50' : 'bg-white hover:shadow-md hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <p className="font-semibold text-sm text-gray-900">{getUserName(comment.userId)}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(comment.createdAt).toLocaleDateString('tr-TR')} - {new Date(comment.createdAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            {comment.editedAt && <span className="ml-1 italic">(düzenlendi)</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {(isNew || (isRecent && !isRead)) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-700 border border-red-300">
                              YENİ
                            </span>
                          )}
                          {canEditComment(comment) && (
                            <>
                              <button
                                data-testid={`comment-edit-${comment.id}`}
                                onClick={() => { setEditingCommentId(comment.id); setEditingCommentContent(comment.content); }}
                                className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                                title="Yorumu düzenle"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                data-testid={`comment-delete-${comment.id}`}
                                onClick={() => handleDeleteComment(comment.id)}
                                className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all"
                                title="Yorumu sil"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {editingCommentId === comment.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editingCommentContent}
                            onChange={(e) => setEditingCommentContent(e.target.value)}
                            className="w-full text-sm border rounded-lg p-2 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                            rows={3}
                          />
                          <div className="flex gap-2">
                            <button onClick={() => handleEditComment(comment.id)} className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">Kaydet</button>
                            <button onClick={() => { setEditingCommentId(null); setEditingCommentContent(''); }} className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">İptal</button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-700 overflow-hidden" dangerouslySetInnerHTML={formatComment(comment.content)} />
                      )}
                    </div>
                  );
                })
              )}
            </div>
            {(user?.permissions?.includes('all') || user?.permissions?.includes('can_comment')) && (
              <div className="mt-6">
                {/* Form - Gradient kenarlık ile */}
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl border-2 border-transparent bg-gradient-to-r from-blue-200 to-cyan-200" style={{
                  background: 'linear-gradient(135deg, #eff6ff 0%, #ecfeff 100%)',
                  borderImage: 'linear-gradient(135deg, #3b82f6, #06b6d4) 1'
                }}>
                  <form onSubmit={handleAddComment} className="space-y-3">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={`Yorum ekleyin...\n\nFormatlama:\n**kalın**  *italik*\n## Başlık\n- Madde listesi`}
                      rows={6}
                      data-testid="comment-input"
                      className="resize-none border-white/50 bg-white/80 backdrop-blur-sm focus:bg-white focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                    />
                    <div className="flex flex-col gap-3">
                      <div className="text-xs text-blue-600 font-medium">
                        **kalın**  *italik*  ## Başlık  - Madde listesi
                      </div>
                      <Button type="submit" className="w-full px-6 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shadow-lg hover:shadow-xl transition-all" size="sm" data-testid="submit-comment-button">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Yorum Ekle
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>

          <div className="border-t-2" style={{ borderImage: 'linear-gradient(135deg, #3b82f6, #06b6d4) 1' }}></div>

          {/* Action Buttons - Checkout/Revision/Complete */}
          {/* Checkout Button - Workflow görevleri için */}
          {((task.assignedTo === user.id || user.role === 'admin') && 
            task.status?.toLowerCase() !== 'completed' && 
            task.status?.toLowerCase() !== 'tamamlandı' && 
            task.workflowId && 
            task.workflowId !== 'none' && 
            task.workflowId !== '') && (
            <div className="glass rounded-2xl p-6">
              <div className="flex flex-col gap-3">
                {task.currentRevision ? (
                  <>
                    {canResolveRevision() && (
                      <Button 
                        onClick={() => setConfirmAction({
                          type: 'resolve_revision',
                          title: 'Revizyon Tamamlama',
                          message: 'Revizyonu tamamlamak istediğinizden emin misiniz? Görev gönderene geri dönecektir.',
                          onConfirm: handleResolveRevision
                        })}
                        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-lg py-6"
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Revizyon Tamamlandı
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button onClick={() => setConfirmAction({
                      type: 'checkout',
                      title: 'Adımı Tamamla',
                      message: 'Bu adımı tamamlayıp sonraki adıma geçmek istediğinizden emin misiniz?',
                      onConfirm: handleCheckoutClick
                    })} className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-lg py-6" data-testid="checkout-button">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Tamamla ve Sonraki Adıma Geç
                    </Button>
                    {canSendRevision() && (
                      <Button 
                        onClick={() => setShowRevisionDialog(true)}
                        className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-lg py-6"
                      >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Revizyona Gönder
                      </Button>
                    )}
                  </>
                )}
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">
                {task.currentRevision 
                  ? 'Revizyon tamamlandıktan sonra görev gönderene geri dönecektir'
                  : (task.assignedTo === user.id 
                    ? 'Bu adımı tamamlayıp sonraki kişiye/adıma göndereceksiniz'
                    : '(Admin yetkisi ile) Bu adımı tamamlayıp sonraki kişiye/adıma göndereceksiniz')
                }
              </p>
            </div>
          )}

          {/* Admin Revizyona Gönder - Tamamlanmış iş akışlı görevler için */}
          {user.role === 'admin' && 
            (task.status?.toLowerCase() === 'completed' || task.status?.toLowerCase() === 'tamamlandı') && 
            task.workflowId && 
            task.workflowId !== 'none' && 
            task.workflowId !== '' &&
            !task.currentRevision &&
            canSendRevision() && (
            <div className="glass rounded-2xl p-6">
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => setShowRevisionDialog(true)}
                  className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-lg py-6"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Revizyona Gönder
                </Button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-3">
                Bu görev tamamlanmış. Sadece revizyona gönderebilirsiniz.
              </p>
            </div>
          )}

          {/* Manuel Atama - Complete/Review Buttons */}
          {(task.assignedTo === user.id || user.role === 'admin') && 
            task.status !== 'completed' && 
            (!task.workflowId || task.workflowId === 'none' || task.workflowId === '') && (
            <div className="glass rounded-2xl p-6">
              <div className="flex flex-col gap-3">
                {task.currentRevision ? (
                  <>
                    {canResolveRevision() && (
                      <Button 
                        onClick={() => setConfirmAction({
                          type: 'resolve_revision',
                          title: 'Revizyon Tamamlama',
                          message: 'Revizyonu tamamlamak istediğinizden emin misiniz? Görev gönderene geri dönecektir.',
                          onConfirm: handleResolveRevision
                        })}
                        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-lg py-6"
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Revizyon Tamamlandı
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {task.status !== 'completed' && task.status !== 'tamamlandı' && (
                      <Button 
                        onClick={() => {
                          if (user?.role === 'admin') {
                            setConfirmAction({
                              type: 'complete_task',
                              title: 'Görevi Tamamla',
                              message: 'Bu görevi tamamlamak istediğinizden emin misiniz?',
                              onConfirm: async () => {
                                try {
                                  await axios.patch(`${API}/tasks/${taskId}`, { status: 'completed' });
                                  toast.success('Görev tamamlandı');
                                  fetchTask();
                                } catch (error) {
                                  toast.error('Görev tamamlanamadı');
                                }
                              }
                            });
                            return;
                          }
                          if (!task.workflowId) {
                            prepareReviewOptions();
                            setShowCompleteDialog(true);
                          } else {
                            setConfirmAction({
                              type: 'complete_task',
                              title: 'Görevi Tamamla',
                              message: 'Bu görevi tamamlamak istediğinizden emin misiniz?',
                              onConfirm: async () => {
                                try {
                                  await axios.patch(`${API}/tasks/${taskId}`, { status: 'completed' });
                                  toast.success('Görev tamamlandı');
                                  fetchTask();
                                } catch (error) {
                                  toast.error('Görev tamamlanamadı');
                                }
                              }
                            });
                          }
                        }}
                        className={`${canSendRevision() ? 'w-full' : 'w-full'} bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-lg py-6`}
                        data-testid="complete-button"
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Görevi Tamamla
                      </Button>
                    )}
                    {canSendRevision() && (
                      <Button 
                        onClick={() => setShowRevisionDialog(true)}
                        className={`w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-lg py-6`}
                      >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Revizyona Gönder
                      </Button>
                    )}
                  </>
                )}
              </div>
              <p className="text-xs text-gray-500 text-center mt-3">
                {task.currentRevision
                  ? 'Revizyon tamamlandıktan sonra görev gönderene geri dönecektir'
                  : (task.status === 'completed' || task.status === 'tamamlandı'
                    ? 'Bu görev tamamlanmış. Sadece revizyona gönderebilirsiniz.'
                    : (task.assignedTo === user.id 
                      ? 'Görevi tamamlayabilir veya inceleme için gönderebilirsiniz'
                      : '(Admin yetkisi ile) Görevi tamamlandı olarak işaretleyeceksiniz')
                  )
                }
              </p>
            </div>
          )}

          <div className="border-t-2" style={{ borderImage: 'linear-gradient(135deg, #3b82f6, #06b6d4) 1' }}></div>

          {/* Files */}
          <div 
            ref={filesRef} 
            className={`glass rounded-2xl p-6 transition-all duration-500 ${
              highlightSection === 'files' ? 'ring-4 ring-yellow-400 bg-yellow-50' : ''
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">
                Dosyalar
                {fileSummary && (
                  <span className="text-sm font-normal text-gray-500 ml-1">
                    ({fileSummary})
                  </span>
                )}
              </h3>
              {(user?.permissions?.includes('all') || user?.permissions?.includes('can_upload_files')) && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={uploading} 
                  onClick={() => setShowFileSelector(true)}
                  data-testid="add-file-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Yükleniyor...' : 'Dosya Ekle'}
                </Button>
              )}
            </div>
            
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Maksimum dosya boyutu:</strong> {project?.max_file_size_mb || 50} MB
              </p>
            </div>
            
            {uploading && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Yükleniyor...</span>
                  <span className="text-sm text-cyan-600 font-semibold">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              {files.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Henüz dosya yok</p>
              ) : (
                files.map((file) => {
                  const isNew = highlightSection === 'files' && highlightItemId === file.id;
                  const fileDate = new Date(file.createdAt);
                  const isRecent = (Date.now() - fileDate.getTime()) < 24 * 60 * 60 * 1000;
                  const isRead = readItems.has(file.id);
                  
                  return (
                    <div 
                      key={file.id} 
                      id={`file-${file.id}`}
                      data-item-id={file.id}
                      data-item-type="file"
                      className={`flex items-center justify-between gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg transition-all duration-500 ${
                        isNew ? 'bg-yellow-100 ring-2 ring-yellow-400' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 overflow-hidden">
                        {(() => {
                          const { Icon: FileIcon, color, bgColor } = getFileIcon(file.filename);
                          const extension = getFileExtension(file.filename);
                          return (
                            <div className="relative">
                              <div className={`w-10 h-10 bg-gradient-to-br ${bgColor} rounded-lg flex items-center justify-center shadow-sm`}>
                                <FileIcon className={`w-5 h-5 ${color}`} />
                              </div>
                              {extension && (
                                <div className="absolute -bottom-1 -right-1 text-[7px] font-bold px-1 py-0.5 rounded bg-white text-gray-600 border border-gray-200 shadow-sm">
                                  {extension.toUpperCase()}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-gray-900 overflow-hidden whitespace-nowrap text-ellipsis max-w-[150px] sm:max-w-[200px]" title={file.filename}>{file.filename}</p>
                            {(isNew || (isRecent && !isRead)) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-red-100 text-red-700 border border-red-300">
                                YENİ
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">
                            {getUserName(file.uploadedBy)} • {new Date(file.createdAt).toLocaleDateString('tr-TR')}
                            {file.fileSize && ` • ${(file.fileSize / 1024 / 1024).toFixed(2)} MB`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const downloadUrl = `${API}/files/${file.id}/download?token=${localStorage.getItem('session_token') || ''}`;
                            const link = document.createElement('a');
                            link.href = downloadUrl;
                            link.download = file.filename || '';
                            link.style.display = 'none';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            toast.success('Dosya indiriliyor');
                          }}
                          className="text-cyan-600 hover:text-cyan-700"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {user?.permissions?.includes('can_delete_files') && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteFile(file.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {/* Edit Task Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Görevi Düzenle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div>
              <Label htmlFor="edit-title">Görev Adı</Label>
              <Input
                id="edit-title"
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Açıklama</Label>
              <div className="mt-1 p-3 rounded-xl" style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #ecfeff 100%)' }}>
                <Textarea
                  id="edit-description"
                  data-testid="edit-description-input"
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  placeholder={`Açıklama ekleyin...\n\nFormatlama:\n**kalın**  *italik*\n## Başlık\n- Madde listesi`}
                  rows={6}
                  className="resize-none border-white/50 bg-white/80 backdrop-blur-sm focus:bg-white focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                />
                <div className="mt-2 text-xs text-blue-600 font-medium">
                  **kalın**  *italik*  ## Başlık  - Madde listesi
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                İptal
              </Button>
              <Button type="submit">
                Kaydet
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Task Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Görevi Sil</DialogTitle>
            <DialogDescription>
              Bu görevi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve görevle ilgili tüm yorumlar, dosyalar ve geçmiş bilgileri silinecektir.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteTask} disabled={deleting}>
              {deleting ? 'Siliniyor...' : 'Evet, Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Dialog - Atama değişikliği (Sadece Admin) */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {task?.workflowId ? 'Adım Atamasını Değiştir' : 'Görev Atamasını Değiştir'}
            </DialogTitle>
            <DialogDescription>
              {task?.workflowId 
                ? 'Mevcut adımı başka bir kullanıcıya atayın. Eski kullanıcı fokusundan görev çıkacaktır.'
                : 'Görevi başka bir kullanıcıya atayın. Eski kullanıcı fokusundan görev çıkacaktır.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Mevcut Atanan Bilgisi */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Mevcut Atanan:</p>
              <p className="font-medium">
                {reassignData.oldUserName || users.find(u => u.id === task?.assignedTo)?.name || 'Bilinmeyen'}
              </p>
            </div>

            {/* Yeni Kullanıcı Seçimi */}
            <div>
              <Label htmlFor="reassign-user">Yeni Kullanıcı *</Label>
              <Select 
                value={reassignData.newUserId} 
                onValueChange={(value) => setReassignData({...reassignData, newUserId: value})}
              >
                <SelectTrigger id="reassign-user" className="mt-2">
                  <SelectValue placeholder="Kullanıcı seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {(() => {
                    const filteredUsers = availableUsers.filter(u => {
                      const isActive = u.status !== 'inactive';
                      const isNotOldUser = u.id !== reassignData.oldUserId;
                      return isActive && isNotOldUser;
                    });
                    
                    return filteredUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} - {u.role}
                      </SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>

            {/* Neden (Opsiyonel) */}
            <div>
              <Label htmlFor="reassign-reason">Neden (Opsiyonel)</Label>
              <Textarea
                id="reassign-reason"
                value={reassignData.reason}
                onChange={(e) => setReassignData({...reassignData, reason: e.target.value})}
                placeholder="Atama değişikliği nedenini belirtebilirsiniz..."
                className="mt-2"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setShowReassignDialog(false);
                setReassignData({ newUserId: '', reason: '', assignmentIndex: null, oldUserId: null, oldUserName: null });
              }}
              disabled={reassigning}
            >
              İptal
            </Button>
            <Button 
              type="button"
              onClick={handleReassign} 
              disabled={reassigning || !reassignData.newUserId}
            >
              {reassigning ? 'Değiştiriliyor...' : 'Atamayı Değiştir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Quantity Dialog - Takım üyesi için miktar düzenleme */}
      <Dialog open={showEditQuantityDialog} onOpenChange={setShowEditQuantityDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Miktar Değerini Düzenle</DialogTitle>
            <DialogDescription>
              Sadece miktar değerini güncelleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveQuantity}>
            <div className="py-4">
              <Label htmlFor="edit-quantity-value">
                Miktar Değeri {task?.quantityType === 'süre' ? '(SS:DD:SS)' : '(Adet)'}
              </Label>
              <Input
                id="edit-quantity-value"
                type="text"
                value={editQuantityValue}
                onChange={(e) => setEditQuantityValue(e.target.value)}
                placeholder={task?.quantityType === 'süre' ? '02:30:00' : '10'}
                required
                className="mt-2"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditQuantityDialog(false)}>
                İptal
              </Button>
              <Button type="submit">
                Kaydet
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* File Selector Modal */}
      {/* Main File Selector Modal - Custom (No Dialog Wrapper) */}
      {showFileSelector && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/15"
            onClick={() => setShowFileSelector(false)}
          />
          
          {/* Modal Container */}
          <div className="relative z-[10000] w-full max-w-2xl h-[85vh] sm:h-[90vh] mx-4 bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden">
            {/* Close Button */}
            <button
              onClick={() => setShowFileSelector(false)}
              className="absolute right-3 top-3 z-[10001] p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
            >
              <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
            </button>
            
            <FileSelector
              onFileSelect={handleAttachExistingFile}
              onNewFileUpload={(file) => {
                // Yeni dosya seçildiğinde file input'u trigger et
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                if (fileInputRef.current) {
                  fileInputRef.current.files = dataTransfer.files;
                  fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }}
              onClose={() => setShowFileSelector(false)}
              uploading={uploading}
              uploadProgress={uploadProgress}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Revision File Selector Modal */}
      {/* Custom Modal for File Selector - No Dialog Wrapper */}
      {showRevisionFileSelector && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/15"
            onClick={() => setShowRevisionFileSelector(false)}
          />
          
          {/* Modal Container */}
          <div className="relative z-[10000] w-full max-w-2xl h-[85vh] sm:h-[90vh] mx-4 bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden">
            {/* Close Button */}
            <button
              onClick={() => setShowRevisionFileSelector(false)}
              className="absolute right-3 top-3 z-[10001] p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
            >
              <X className="w-4 h-4 text-gray-600 hover:text-gray-900" />
            </button>
            
            <FileSelector
              onFileSelect={(file) => {
                // Mevcut dosya seçildiğinde revisionFiles'a ekle
                // File objesini simüle et
                const simulatedFile = new File([], file.filename, { type: file.fileType || 'application/octet-stream' });
                simulatedFile.isExisting = true;
                simulatedFile.existingFileId = file.id;
                setRevisionFiles(prev => [...prev, simulatedFile]);
                setShowRevisionFileSelector(false);
                toast.success('Dosya eklendi');
              }}
              onNewFileUpload={(file) => {
                // Yeni dosya seçildiğinde revisionFiles'a ekle
                setRevisionFiles(prev => [...prev, file]);
                setShowRevisionFileSelector(false);
              }}
              onClose={() => setShowRevisionFileSelector(false)}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Adım Tamamlama Modal */}
      {/* Adım Miktar Düzenleme Modal */}
      <Dialog open={showEditStepQuantityDialog} onOpenChange={setShowEditStepQuantityDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adım Miktarını Düzenle</DialogTitle>
            <DialogDescription>
              Bu adımın kategori ve miktar bilgilerini güncelleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-step-tag">Kategori</Label>
              <Select 
                value={editStepQuantityData.tagId} 
                onValueChange={(value) => setEditStepQuantityData(prev => ({ ...prev, tagId: value }))}
              >
                <SelectTrigger id="edit-step-tag">
                  <SelectValue placeholder="Kategori seçin" />
                </SelectTrigger>
                <SelectContent>
                  {tags.map(tag => (
                    <SelectItem key={tag.id} value={tag.id}>{tag.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-step-quantity-type">Miktar Tipi</Label>
              <Select 
                value={editStepQuantityData.quantityType} 
                onValueChange={(value) => setEditStepQuantityData(prev => ({ ...prev, quantityType: value }))}
              >
                <SelectTrigger id="edit-step-quantity-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="adet">Adet</SelectItem>
                  <SelectItem value="süre">Süre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-step-quantity-value">
                {editStepQuantityData.quantityType === 'süre' ? 'Süre (SS:DD:SS)' : 'Miktar Değeri'}
              </Label>
              <Input
                id="edit-step-quantity-value"
                type="text"
                placeholder={editStepQuantityData.quantityType === 'süre' ? '02:30:00' : '10'}
                value={editStepQuantityData.quantityValue}
                onChange={(e) => setEditStepQuantityData(prev => ({ ...prev, quantityValue: e.target.value }))}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditStepQuantityDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleUpdateStepQuantity} className="bg-gradient-to-r from-cyan-600 to-blue-600">
              Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden file input */}
      <input 
        ref={fileInputRef}
        type="file" 
        className="hidden" 
        onChange={handleFileUpload} 
        disabled={uploading} 
      />

      {/* Paylaşım Modalı */}
      {showShareModal && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowShareModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col" data-testid="share-modal">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-bold text-gray-900">Görevi Paylaş</h3>
              </div>
              <button onClick={() => setShowShareModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-100 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Paylaşım Notu <span className="text-red-500">*</span></label>
                <textarea
                  data-testid="share-note-input"
                  placeholder="Paylaşım notunuzu yazın..."
                  value={shareNote}
                  onChange={(e) => setShareNote(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-y min-h-[60px]"
                />
              </div>
              <input
                data-testid="share-search-input"
                type="text"
                placeholder="Kullanıcı ara..."
                value={shareSearchQuery}
                onChange={(e) => setShareSearchQuery(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-2" style={{ maxHeight: '400px' }}>
              {shareUsers
                .filter(u => {
                  const q = shareSearchQuery.toLowerCase();
                  return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
                })
                .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'))
                .map(u => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-indigo-700 font-semibold text-sm">{(u.name || '?')[0].toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                    </div>
                    <button
                      data-testid={`share-user-${u.id}`}
                      onClick={() => handleShareTask(u.id)}
                      disabled={sharingUserId === u.id}
                      className="flex-shrink-0 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-xs font-semibold rounded-lg transition-colors"
                    >
                      {sharingUserId === u.id ? 'Gönderiliyor...' : 'Paylaş'}
                    </button>
                  </div>
                ))
              }
              {shareUsers.filter(u => {
                const q = shareSearchQuery.toLowerCase();
                return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
              }).length === 0 && (
                <p className="text-center text-gray-500 text-sm py-8">Kullanıcı bulunamadı</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* "Beklemede" -> "İşlemde" Görev Başlatma Modalı */}
      {showStartTaskModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10002] flex items-center justify-center" data-testid="start-task-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowStartTaskModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Görevi Başlat</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Bu görevi başlatmak ve <span className="font-semibold text-amber-700">İşlemde</span>'ye almak istiyor musun? Görevi <span className="font-semibold">{userViewCount}</span> defa görüntüledin{viewCount > userViewCount ? <> (toplam <span className="font-semibold">{viewCount}</span> defa görüntülendi)</> : null}.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-2">
              <Button
                variant="outline"
                onClick={() => setShowStartTaskModal(false)}
                className="flex-1"
                data-testid="start-task-cancel-btn"
              >
                Hayır
              </Button>
              <Button
                onClick={handleConfirmStartTask}
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                data-testid="start-task-confirm-btn"
              >
                Evet, Başlat
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Onay Modalı */}
      {confirmAction && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10001] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmAction(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6" data-testid="confirm-action-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{confirmAction.title}</h3>
            <p className="text-sm text-gray-600 mb-6">{confirmAction.message}</p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setConfirmAction(null)}
                className="flex-1"
                data-testid="confirm-cancel-btn"
              >
                İptal
              </Button>
              <Button
                onClick={async () => {
                  const fn = confirmAction.onConfirm;
                  setConfirmAction(null);
                  await fn();
                }}
                className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                data-testid="confirm-ok-btn"
              >
                Tamam
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
